import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import ModalPortal from '../components/ModalPortal';
import EditarVendaModal from '../components/EditarVendaModal';
import { parseProdutosSelecionados } from '../components/ProdutosPfEditor';
import { MeiosEnvioList } from '../components/MeioEnvioLabel';
import { salesService, usersService } from '../services/api';
import api from '../services/api';
import { formatDate, formatDateOnly, formatDateTime, formatPhone, formatCPF, formatDocumento } from '../utils/masks';
import { parseObservacoesTratamento } from '../utils/observacoesTratamento';
import { useAuth } from '../contexts/AuthContext';
import '../styles/FilaTratamento.css';

import { formatMarcaLabel } from '../utils/marcas';

const formatMarca = (marca) => formatMarcaLabel(marca);

// Som de notificação (beep via Web Audio API)
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
    setTimeout(() => {
      const o2 = audioContext.createOscillator();
      const g2 = audioContext.createGain();
      o2.connect(g2);
      g2.connect(audioContext.destination);
      o2.frequency.value = 1000;
      o2.type = 'sine';
      g2.gain.setValueAtTime(0.3, audioContext.currentTime + 0.25);
      g2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.45);
      o2.start(audioContext.currentTime + 0.25);
      o2.stop(audioContext.currentTime + 0.45);
    }, 200);
  } catch (e) {
    console.warn('Não foi possível reproduzir o som:', e);
  }
};

// Mostrar notificação do navegador
const showNewSaleNotification = (quantidade) => {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    const title = 'Nova venda na fila';
    const body = quantidade === 1
      ? 'Uma nova venda entrou na fila de tratamento.'
      : `${quantidade} novas vendas entraram na fila de tratamento.`;
    new Notification(title, { body, icon: '/favicon.ico' });
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission();
  }
};

// Obter URL base da API a partir da configuração do axios
const getApiBaseUrl = () => {
  // Usar a mesma baseURL configurada no axios
  return api.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:9040';
};

// Data de hoje em Brasília no formato YYYY-MM-DD (para data_instalacao)
const getTodayBrasilia = () => {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
};

const FilaTratamento = () => {
  const { user } = useAuth();
  const [vendas, setVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('pendente_bko');
  const [busca, setBusca] = useState('');
  const [filtroVendedor, setFiltroVendedor] = useState('');
  const [filtroTipoCliente, setFiltroTipoCliente] = useState('');
  const [filtroMarca, setFiltroMarca] = useState('');
  const [vendedores, setVendedores] = useState([]);
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [tratamentoData, setTratamentoData] = useState({
    status: '',
    observacao_tratamento: '',
    data_instalacao: '',
    numero_ordem: '',
    data_conclusao: '',
    simulacao: '',
    link_aceite: '',
    motivo_imagem: '',
    numero_pedido_pap: ''
  });
  const [imagemPreview, setImagemPreview] = useState(null);
  const [imagemFile, setImagemFile] = useState(null);
  const [novaObservacaoSupervisao, setNovaObservacaoSupervisao] = useState('');
  const [addingObservacaoSupervisao, setAddingObservacaoSupervisao] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [visualNotification, setVisualNotification] = useState(null); // { quantidade, show }
  const visualNotificationTimerRef = useRef(null);
  const previousIdsRef = useRef(null);
  const [vendasAnteriores, setVendasAnteriores] = useState(null); // { telefone, cpf, email } -> [venda, ...]
  const [buscandoVendasAnteriores, setBuscandoVendasAnteriores] = useState(false);
  const [vendaParaMostrarHistorico, setVendaParaMostrarHistorico] = useState(null);
  const [vendaSelecionadaParaModal, setVendaSelecionadaParaModal] = useState(null); // Modal completo de detalhes
  const [somenteVisualizacao, setSomenteVisualizacao] = useState(false);
  const [validacaoOkModal, setValidacaoOkModal] = useState(null); // null | 'confirm' | 'dados'
  const [editandoValidacaoOk, setEditandoValidacaoOk] = useState(null);
  const [filaVisao, setFilaVisao] = useState(null); // { ativa, automatica, manual, ate }
  const [togglingVerTudo, setTogglingVerTudo] = useState(false);

  // Solicitar permissão para notificações ao montar a página
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Limpar timer da notificação visual ao desmontar
  useEffect(() => {
    return () => {
      if (visualNotificationTimerRef.current) {
        clearTimeout(visualNotificationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadFila();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus, busca, filtroVendedor, filtroTipoCliente, filtroMarca]);

  useEffect(() => {
    loadVendedores();
  }, []);

  // Atualização automática da fila a cada 10 segundos
  useEffect(() => {
    const intervalId = setInterval(() => {
      // Só atualiza se não houver modal aberto e não estiver carregando
      if (!selectedVenda && !loading && !updating) {
        loadFilaSilencioso();
      }
    }, 10000); // 10 segundos

    // Limpar interval quando o componente desmontar ou quando o filtro mudar
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroStatus, busca, filtroVendedor, filtroTipoCliente, filtroMarca, selectedVenda, loading, updating]);

  const loadFilaVisao = async () => {
    try {
      const estado = await salesService.getFilaVisao();
      setFilaVisao(estado);
    } catch (error) {
      console.error('Erro ao carregar visão da fila:', error);
    }
  };

  const handleToggleVerTudo = async () => {
    if (!['admin', 'diretor'].includes(user?.role)) return;
    const ativar = !(filaVisao?.manual);
    try {
      setTogglingVerTudo(true);
      const estado = await salesService.setFilaVerTudo(ativar);
      setFilaVisao({
        ativa: estado.ativa,
        automatica: estado.automatica,
        manual: estado.manual,
        ate: estado.ate
      });
      await loadFilaSilencioso();
    } catch (error) {
      console.error('Erro ao alterar Ver tudo:', error);
      alert(error.response?.data?.error || 'Erro ao alterar visão completa da fila');
    } finally {
      setTogglingVerTudo(false);
    }
  };

  const getFilaVisaoMensagem = () => {
    if (!filaVisao?.ativa) return null;
    if (filaVisao.automatica && filaVisao.manual) {
      return 'Visão completa ativa até 00:00 (São Paulo) — horário noturno e botão Ver tudo.';
    }
    if (filaVisao.automatica) {
      return 'Visão completa automática até 00:00 (São Paulo) — horário 19:00–00:00.';
    }
    return 'Visão completa ativa pelo botão Ver tudo até 00:00 (São Paulo).';
  };

  // Função para carregar fila silenciosamente (sem mostrar loading)
  const loadFilaSilencioso = async () => {
    try {
      const filters = { 
        status: filtroStatus === 'all' ? 'all' : filtroStatus 
      };
      if (busca) filters.busca = busca;
      if (filtroVendedor) filters.vendedor_id = filtroVendedor;
      if (filtroTipoCliente) filters.tipo_cliente = filtroTipoCliente;
      if (filtroMarca) filters.marca = filtroMarca;
      const [data] = await Promise.all([
        salesService.getQueue(filters),
        loadFilaVisao()
      ]);
      const currentIds = new Set((data || []).map((v) => v.id));
      const previousIds = previousIdsRef.current;

      // Se já tínhamos uma lista anterior (não é a primeira carga), verificar novas vendas
      if (previousIds !== null && previousIds.size > 0) {
        const newIds = [...currentIds].filter((id) => !previousIds.has(id));
        // Notificações (som + visual) para admin, diretor, backoffice e supervisor_bko
        const deveNotificar = ['admin', 'diretor', 'backoffice', 'supervisor_bko'].includes(user?.role);
        if (newIds.length > 0 && deveNotificar) {
          playNotificationSound();
          showNewSaleNotification(newIds.length);
          // Notificação visual na página (toast)
          if (visualNotificationTimerRef.current) clearTimeout(visualNotificationTimerRef.current);
          setVisualNotification({ quantidade: newIds.length });
          visualNotificationTimerRef.current = setTimeout(() => {
            setVisualNotification(null);
            visualNotificationTimerRef.current = null;
          }, 6000);
        }
      }

      previousIdsRef.current = currentIds;
      setVendas(data);
    } catch (error) {
      console.error('Erro ao atualizar fila:', error);
      // Não mostrar alerta em atualizações silenciosas
    }
  };

  const loadFila = async () => {
    try {
      setLoading(true);
      const filters = { 
        status: filtroStatus === 'all' ? 'all' : filtroStatus 
      };
      if (busca) filters.busca = busca;
      if (filtroVendedor) filters.vendedor_id = filtroVendedor;
      if (filtroTipoCliente) filters.tipo_cliente = filtroTipoCliente;
      if (filtroMarca) filters.marca = filtroMarca;
      const [data] = await Promise.all([
        salesService.getQueue(filters),
        loadFilaVisao()
      ]);
      const ids = new Set((data || []).map((v) => v.id));
      previousIdsRef.current = ids;
      setVendas(data);
    } catch (error) {
      console.error('Erro ao carregar fila:', error);
      alert('Erro ao carregar fila de tratamento');
    } finally {
      setLoading(false);
    }
  };

  const loadVendedores = async () => {
    try {
      const data = await usersService.getAll();
      setVendedores(data);
    } catch (error) {
      console.error('Erro ao carregar vendedores:', error);
    }
  };

  const getStatusClass = (status) => {
    const classes = {
      'pendente_bko': 'status-pendente-bko',
      'tratando': 'status-tratando',
      'pendente_vendedor': 'status-pendente-vendedor',
      'pendente_sistemica': 'status-pendente-sistemica',
      'pendente_biometria': 'status-pendente-biometria',
      'pendente_auditoria': 'status-pendente-auditoria',
      'pendente_imput': 'status-pendente-imput',
      'tramitacao_manual': 'status-tramitacao-manual',
      'critica': 'status-critica',
      'mesa_de_credito': 'status-mesa-de-credito',
      'aguardando_whatsapp': 'status-aguardando-whatsapp',
      'aguardando_dados_sfa': 'status-aguardando-dados-sfa',
      'aguardando_instalacao': 'status-aguardando-instalacao',
      'validacao_ok': 'status-validacao-ok',
      'instalacao_cancelada': 'status-instalacao-cancelada',
      'concluido': 'status-concluido',
      'cancelado': 'status-cancelado',
      'desistencia': 'status-desistencia',
      'estorno': 'status-estorno',
      'aguardando_aceite': 'status-aguardando-aceite',
      'sem_viabilidade': 'status-sem-viabilidade',
      'aguardando_estruturante': 'status-aguardando-estruturante',
      'aguardando_can_ou_reag': 'status-aguardando-can-ou-reag',
      'cancelamento_comercial': 'status-cancelamento-comercial',
      'cancelamento_tecnico': 'status-cancelamento-tecnico'
    };
    return classes[status] || '';
  };

  const getStatusText = (status) => {
    const texts = {
      'pendente_bko': 'Pendente BKO',
      'tratando': 'Tratando',
      'pendente_vendedor': 'Pendente Vendedor',
      'pendente_sistemica': 'Pendente Sistêmica',
      'pendente_biometria': 'Pendente Biometria',
      'pendente_auditoria': 'Pendente Auditoria',
      'pendente_imput': 'Pendente Imput',
      'tramitacao_manual': 'Tramitação Manual',
      'critica': 'Crítica',
      'mesa_de_credito': 'Mesa de Crédito',
      'aguardando_whatsapp': 'Aguardando WhatsApp',
      'aguardando_dados_sfa': 'Aguardando dados SFA',
      'aguardando_instalacao': 'Aguardando Instalação',
      'validacao_ok': 'Validação OK',
      'instalacao_cancelada': 'Instalação cancelada',
      'concluido': 'Concluído',
      'cancelado': 'Cancelado',
      'desistencia': 'Desistencia',
      'estorno': 'Estorno',
      'aguardando_aceite': 'Aguardando Aceite',
      'sem_viabilidade': 'Sem Viabilidade',
      'aguardando_estruturante': 'Aguardando Estruturante',
      'aguardando_can_ou_reag': 'Aguardando CAN ou REAG',
      'cancelamento_comercial': 'Cancelamento comercial',
      'cancelamento_tecnico': 'Cancelamento técnico'
    };
    return texts[status] || status;
  };

  // Função para calcular tempo de espera
  const calcularTempoEspera = (statusEnteredAt) => {
    if (!statusEnteredAt) return '-';
    
    const agora = new Date();
    const entrada = new Date(statusEnteredAt);
    const diffMs = agora - entrada;
    const diffMinutos = Math.floor(diffMs / 60000);
    const diffHoras = Math.floor(diffMinutos / 60);
    const diffDias = Math.floor(diffHoras / 24);
    
    if (diffDias > 0) {
      return `${diffDias}d ${diffHoras % 24}h`;
    } else if (diffHoras > 0) {
      return `${diffHoras}h ${diffMinutos % 60}min`;
    } else {
      return `${diffMinutos}min`;
    }
  };

  // Minutos aguardando na fila (a partir de status_entered_at)
  const getMinutosEspera = (statusEnteredAt) => {
    if (!statusEnteredAt) return null;
    return Math.floor((new Date() - new Date(statusEnteredAt)) / 60000);
  };

  // Nível/cor de urgência conforme tempo aguardando na fila
  // < 30min: no prazo | 30min-2h: atenção | 2h-1d: urgente | 1d+: crítico
  const getNivelEspera = (statusEnteredAt) => {
    const min = getMinutosEspera(statusEnteredAt);
    if (min == null) return { nivel: 'sem', cor: 'var(--texto-secundario)' };
    if (min >= 1440) return { nivel: 'critico', cor: '#ff6b6b' };
    if (min >= 120) return { nivel: 'urgente', cor: '#ffa500' };
    if (min >= 30) return { nivel: 'atencao', cor: '#ffd43b' };
    return { nivel: 'no-prazo', cor: '#51cf66' };
  };

  // Venda já em tratamento por outro BKO: só consulta, sem assumir nem alterar status
  const vendaSomenteLeitura = (venda) => {
    if (user?.role !== 'backoffice' || venda?.status !== 'tratando') return false;
    const tratadorId = venda.tratador?.id ?? venda.tratado_por;
    return Number(tratadorId) !== Number(user?.id);
  };

  const openTratamento = async (venda) => {
    try {
      if (vendaSomenteLeitura(venda)) {
        await visualizarVenda(venda);
        return;
      }

      setSomenteVisualizacao(false);
      // Verificar se é backoffice e se já está tratando outra venda
      if (user?.role === 'backoffice' && venda.status !== 'tratando') {
        // Verificar se há outra venda em "tratando" atribuída ao usuário atual
        const vendaEmTratamento = vendas.find(
          v => v.status === 'tratando' && 
               v.tratador?.id === user.id && 
               v.id !== venda.id
        );

        if (vendaEmTratamento) {
          alert(`⚠️ Você já está tratando a venda #${vendaEmTratamento.id}. Finalize o tratamento da venda atual antes de iniciar outro.`);
          // Mudar o filtro para mostrar a venda em tratamento
          setFiltroStatus('tratando');
          return;
        }
      }

      // Mudar status automaticamente para "tratando" quando abrir o modal
      // mas apenas se não estiver já em "tratando" ou em um status final
      if (venda.status !== 'tratando' && 
          venda.status !== 'concluido' && 
          venda.status !== 'cancelado') {
        try {
          await salesService.updateStatus(venda.id, 'tratando', '');
        } catch (error) {
          // Se o erro for que já está tratando outra venda, mostrar mensagem
          if (error.response?.status === 403 && error.response?.data?.error) {
            alert(`⚠️ ${error.response.data.error}`);
            if (error.response.data.venda_id) {
              // Mudar o filtro para mostrar a venda em tratamento
              setFiltroStatus('tratando');
            }
            return;
          }
          throw error;
        }
      }
      
      // Carregar dados completos da venda (com status atualizado)
      const vendaCompleta = await salesService.getById(venda.id);
      setSelectedVenda(vendaCompleta);
      
      // Log para depuração dos dados PAP
      console.log('📋 DADOS PAP DA VENDA', vendaCompleta.id, ':', {
        tipo_cliente: vendaCompleta.tipo_cliente,
        vendedor_codigo_pap: vendaCompleta.vendedor_codigo_pap,
        vendedor_login_pap: vendaCompleta.vendedor_login_pap,
        numero_pedido_pap: vendaCompleta.numero_pedido_pap
      });
      
      const dataInstalacaoInicial = vendaCompleta.data_instalacao
        || vendaCompleta.data_agendamento1
        || getTodayBrasilia();
      setTratamentoData({
        status: vendaCompleta.status,
        observacao_tratamento: '', // Campo separado para nova observação adicional
        observacao_historico: vendaCompleta.observacao_tratamento || '', // Histórico completo (somente leitura)
        data_instalacao: dataInstalacaoInicial,
        numero_ordem: vendaCompleta.numero_ordem || '',
        data_conclusao: vendaCompleta.data_conclusao || getTodayBrasilia(),
        simulacao: vendaCompleta.simulacao || '',
        link_aceite: vendaCompleta.link_aceite || '',
        motivo_imagem: vendaCompleta.motivo_imagem || '',
        numero_pedido_pap: vendaCompleta.numero_pedido_pap || ''
      });
      setImagemPreview(null);
      setImagemFile(null);
      
      // Se o filtro atual não inclui "tratando", mudar para "tratando" ou "all"
      // para que a venda permaneça visível na lista
      if (filtroStatus !== 'tratando' && filtroStatus !== 'all' && 
          vendaCompleta.status === 'tratando') {
        setFiltroStatus('tratando');
      }
    } catch (error) {
      console.error('Erro ao carregar dados da venda:', error);
      if (error.response?.data?.error) {
        alert(`❌ ${error.response.data.error}`);
      } else {
        alert('Erro ao carregar dados da venda');
      }
    }
  };

  const visualizarVenda = async (venda) => {
    try {
      const vendaCompleta = await salesService.getById(venda.id);
      setSomenteVisualizacao(true);
      setSelectedVenda(vendaCompleta);
    } catch (error) {
      console.error('Erro ao carregar venda:', error);
      alert('Erro ao carregar detalhes da venda');
    }
  };

  // Buscar vendas anteriores quando abrir o modal
  useEffect(() => {
    if (selectedVenda) {
      buscarVendasAnteriores(selectedVenda);
    }
  }, [selectedVenda]);

  const closeTratamento = () => {
    // Se a venda estava em "tratando", não recarregar a fila
    // Isso permite que o usuário reassuma o tratamento se fechar o modal por acidente
    const estavaTratando = selectedVenda?.status === 'tratando';
    setSomenteVisualizacao(false);
    setSelectedVenda(null);
    setTratamentoData({ status: '', observacao_tratamento: '', data_instalacao: '', numero_ordem: '', data_conclusao: '', simulacao: '', link_aceite: '', motivo_imagem: '', numero_pedido_pap: '' });
    setNovaObservacaoSupervisao('');
    setVendasAnteriores(null);
    setVendaParaMostrarHistorico(null);
    setVendaSelecionadaParaModal(null);
    setValidacaoOkModal(null);
    setEditandoValidacaoOk(null);
    // Se estava tratando, mudar o filtro para "tratando" para manter a venda visível
    if (estavaTratando && filtroStatus !== 'tratando' && filtroStatus !== 'all') {
      setFiltroStatus('tratando');
    }
  };

  // Função para buscar vendas anteriores com mesmo telefone, CPF ou email
  const buscarVendasAnteriores = async (venda) => {
    setBuscandoVendasAnteriores(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      // Adicionar ID da venda atual para excluir
      params.append('vendaAtualId', venda.id);
      
      // Adicionar telefones se existirem
      if (venda.telefone) {
        params.append('telefone', venda.telefone);
      }
      if (venda.telefone2) {
        params.append('telefone2', venda.telefone2);
      }
      
      // Adicionar CPF se existir
      if (venda.cpf) {
        params.append('cpf', venda.cpf);
      } else if (venda.documento) {
        params.append('cpf', venda.documento); // documento pode ser CPF
      }
      
      // Adicionar email se existir
      if (venda.email) {
        params.append('email', venda.email);
      }
      
      const response = await fetch(`${api.defaults.baseURL}/api/sales/contatos-repetidos?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVendasAnteriores(data);
      } else {
        console.warn('Não foi possível buscar vendas anteriores:', response.statusText);
        setVendasAnteriores({});
      }
    } catch (error) {
      console.error('Erro ao buscar vendas anteriores:', error);
      setVendasAnteriores({});
    } finally {
      setBuscandoVendasAnteriores(false);
    }
  };

  // Função auxiliar para verificar se um contato (telefone, CPF ou email) está repetido
  // e obter as vendas anteriores relacionadas
  const verificarContatoRepetido = (tipo, valor) => {
    if (!valor || valor === '-' || !vendasAnteriores) return null;
    const lista = vendasAnteriores[tipo];
    if (!Array.isArray(lista) || lista.length === 0) return null;
    // API já retornou vendas que batem com o contato pesquisado - se há itens, está repetido
    return true;
  };

  // Função para abrir modal de histórico de uma venda específica
  const abrirHistoricoVenda = (venda) => {
    setVendaParaMostrarHistorico(venda);
  };

  // Função para abrir modal de detalhes completo de uma venda
  const abrirModalDetalhesVenda = (venda) => {
    setVendaSelecionadaParaModal(venda);
  };

  // Função para fechar modal de histórico
  const fecharHistoricoVenda = () => {
    setVendaParaMostrarHistorico(null);
    setVendaSelecionadaParaModal(null);
  };

  // Função para fechar modal de detalhes
  const fecharModalDetalhes = () => {
    setVendaSelecionadaParaModal(null);
  };

  // Componente de renderização para campos de contato com destaque (vermelho + lupa se repetido)
  const renderizarCampoContato = (label, valor, tipoContato, valorParaBuscar = null) => {
    if (!valor || valor === '-') {
      return (
        <div className="info-item">
          <strong>{label}:</strong>
          <span>{valor}</span>
        </div>
      );
    }

    // Se não tiver vendas anteriores carregadas, mostrar normal
    if (!vendasAnteriores || Object.keys(vendasAnteriores).length === 0) {
      return (
        <div className="info-item">
          <strong>{label}:</strong>
          <span>{valor}</span>
        </div>
      );
    }

    // Tentar buscar o valor específico se não foi passado
    const valorBusca = valorParaBuscar || valor;
    const repetido = verificarContatoRepetido(tipoContato, valorBusca);

    return (
      <div className={`info-item ${repetido ? 'info-item-contato-repetido' : ''}`}>
        <strong>{label}:</strong>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {valor}
          {repetido && (
            <button
              type="button"
              className="lupa-contato"
              onClick={() => abrirHistoricoVenda(selectedVenda)}
              title="Ver vendas anteriores com este contato"
            >
              🔍
            </button>
          )}
        </span>
      </div>
    );
  };

  const parseObservacoesSupervisao = (texto) => {
    if (!texto || !texto.trim()) return [];
    const linhas = texto.trim().split('\n').filter(l => l.trim());
    const obs = [];
    linhas.forEach(linha => {
      const m = linha.trim().match(/^\[([^\]]+)\]\s*(.+?):\s*(.+)$/);
      if (m) obs.push({ dataHora: m[1], usuario: m[2], texto: m[3] });
      else if (linha.trim()) obs.push({ tipo: 'texto', texto: linha.trim() });
    });
    return obs.reverse();
  };

  const handleAddObservacaoSupervisao = async () => {
    if (!selectedVenda || !novaObservacaoSupervisao.trim()) return;
    setAddingObservacaoSupervisao(true);
    try {
      const { sale } = await salesService.addObservacaoSupervisao(selectedVenda.id, novaObservacaoSupervisao.trim());
      setSelectedVenda(sale);
      setNovaObservacaoSupervisao('');
    } catch (error) {
      console.error('Erro ao adicionar observação:', error);
      alert('Erro ao adicionar observação: ' + (error.response?.data?.error || error.message));
    } finally {
      setAddingObservacaoSupervisao(false);
    }
  };

  const handleTratamentoChange = (e) => {
    const { name, value } = e.target;
    setTratamentoData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'status' && value === 'aguardando_instalacao' && !prev.data_instalacao) {
        next.data_instalacao = selectedVenda?.data_agendamento1 || getTodayBrasilia();
      }
      if (name === 'status' && value === 'validacao_ok') {
        setValidacaoOkModal(null);
      }
      if (name === 'status' && value !== 'validacao_ok') {
        setValidacaoOkModal(null);
      }
      if (name === 'status' && value === 'concluido' && !prev.data_conclusao) {
        next.data_conclusao = getTodayBrasilia();
      }
      return next;
    });
  };

  const handleSelectImagem = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem.');
        return;
      }
      setImagemFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagemPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePasteImagem = (e) => {
    e.preventDefault();
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        setImagemFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagemPreview(reader.result);
        };
        reader.readAsDataURL(file);
        break;
      }
    }
  };

  // Monta e copia a ficha de cadastro no formato solicitado
  const copiarFichaCadastro = () => {
    if (!selectedVenda) return;
    const v = selectedVenda;

    const linha = (label, valor) => `${label}\t${valor || '-'}`;
    const complemento = [v.complemento1, v.complemento2, v.complemento3].filter(Boolean).join(', ') || '-';
    const dataNasc = v.data_nascimento ? formatDateOnly(v.data_nascimento) : '-';
    const dataVenc = v.data_vencimento ? `Dia ${v.data_vencimento}` : '-';
    const observacoes = [v.observacao_venda, v.observacao_tratamento].filter(Boolean).join('\n') || '-';

    let linhas = [
      'FICHA CADASTRO',
      '',
      linha('Nome completo', v.nome_cliente),
      ...(v.tipo_cliente === 'cnpj'
        ? [
            linha('CNPJ', v.documento ? v.documento.replace(/\D/g, '') : null),
            linha('Razão Social', v.razao_social),
            linha('CPF do Representante', v.cpf ? v.cpf.replace(/\D/g, '') : null),
            linha('Nome do Representante', v.nome_representante),
            linha('RG', v.rg),
            linha('Data nascimento', dataNasc),
            linha('Nome da mãe', v.nome_mae)
          ]
        : [
            linha('CPF', (v.documento || v.cpf) ? formatDocumento(v.documento || v.cpf) : null),
            linha('RG', v.rg),
            linha('Data nascimento', dataNasc),
            linha('Nome da mãe', v.nome_mae)
          ]),
      linha('Contato 1', v.tipo_cliente === 'cnpj' ? (v.telefone ? v.telefone.replace(/\D/g, '') : null) : (v.telefone ? formatPhone(v.telefone) : null)),
      linha('Contato 2', v.tipo_cliente === 'cnpj' ? (v.telefone2 ? v.telefone2.replace(/\D/g, '') : null) : (v.telefone2 ? formatPhone(v.telefone2) : null)),
      linha('E-mail', v.email ? v.email.toLowerCase() : null),
      linha('CEP', v.cep),
      linha('Estado', v.estado),
      linha('Cidade', v.cidade),
      linha('Bairro', v.bairro),
      linha('Rua', v.logradouro),
      linha('Número', v.numero),
      linha('Complemento', complemento),
      linha('Data Vencimento', dataVenc),
      linha('Observações', observacoes)
    ];

    const texto = linhas.join('\n');
    navigator.clipboard.writeText(texto).then(() => {
      alert('✅ Ficha de cadastro copiada para a área de transferência!');
    }).catch(() => {
      alert('❌ Não foi possível copiar. Tente selecionar e copiar manualmente.');
    });
  };

  const copiarNeo = () => {
    if (!selectedVenda) return;
    const v = selectedVenda;

    const texto = `Pedido CNPJ
RAZÃO SOCIAL ${v.razao_social || '-'}
CNPJ ${v.documento ? v.documento.replace(/\D/g, '') : '-'}
NÚMERO DA SIMULAÇÃO ${tratamentoData.simulacao || v.simulacao || '-'}`;

    navigator.clipboard.writeText(texto).then(() => {
      alert('✅ Informações copiadas para a área de transferência!');
    }).catch(() => {
      alert('❌ Não foi possível copiar. Tente selecionar e copiar manualmente.');
    });
  };

  const copiarNeoCPF = () => {
    if (!selectedVenda) return;
    const v = selectedVenda;

    // Formatar CPF (apenas números)
    const cpfFormatado = v.tipo_cliente === 'cnpj' 
      ? (v.cpf ? v.cpf.replace(/\D/g, '') : '-') 
      : (v.cpf ? v.cpf.replace(/\D/g, '') : (v.documento ? v.documento.replace(/\D/g, '') : '-'));

    // Formatar telefones (apenas números)
    const telefone1 = v.telefone ? v.telefone.replace(/\D/g, '') : '-';
    const telefone2 = v.telefone2 ? v.telefone2.replace(/\D/g, '') : null;
    const telefones = telefone2 ? `${telefone1} / ${telefone2}` : telefone1;

    // Nome do titular (para CNPJ usa razao_social, para CPF usa nome_cliente)
    const nomeTitular = v.tipo_cliente === 'cnpj' 
      ? (v.razao_social || v.nome_cliente || '-') 
      : (v.nome_cliente || '-');

    // Formatar valor total
    const valorFormatado = v.valor_total 
      ? `R$ ${parseFloat(v.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
      : '-';

    // Incluir dados PAP se existirem e for venda CPF
    const dadosPap = (v.tipo_cliente === 'cpf' && v.vendedor_codigo_pap) 
      ? `CÓDIGO PAP: ${v.vendedor_codigo_pap}
LOGIN PAP: ${v.vendedor_login_pap || '-'}`
      : '';

    const texto = `CPF: ${cpfFormatado}
NOME DO TITULAR: ${nomeTitular}
TELEFONES DE CONTATO: ${telefones}
EMAIL: ${v.email ? v.email.toLowerCase() : '-'}
PRODUTO: ${v.observacao_venda || '-'}
VALOR: ${valorFormatado}
NÚMERO DO PEDIDO PAP: ${v.numero_pedido_pap || '-'}${dadosPap ? '\n' + dadosPap : ''}`;

    navigator.clipboard.writeText(texto).then(() => {
      alert('✅ Informações copiadas para a área de transferência!');
    }).catch(() => {
      alert('❌ Não foi possível copiar. Tente selecionar e copiar manualmente.');
    });
  };

  // Função para baixar Excel da venda Claro via backend
  const downloadExcelClaro = async () => {
    if (!selectedVenda) return;

    try {
      // Fazer requisição ao backend para gerar o Excel
      const token = localStorage.getItem('token');
      const response = await fetch(`${api.defaults.baseURL}/api/sales/${selectedVenda.id}/download-excel-claro`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao gerar Excel');
      }

      // Pegar o blob do arquivo
      const blob = await response.blob();
      
      // Criar URL temporária para download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedVenda.nome_cliente || 'Cliente'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      
      // Limpar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Excel baixado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao baixar Excel:', error);
      alert(`❌ ${error.message}`);
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();

    if (!tratamentoData.status) {
      alert('Selecione um status');
      return;
    }

    if (tratamentoData.status === 'validacao_ok') {
      if (!validacaoOkModal) {
        setValidacaoOkModal('confirm');
        return;
      }
      if (validacaoOkModal === 'confirm') {
        return;
      }
      if (validacaoOkModal === 'dados') {
        if (!tratamentoData.data_instalacao) {
          alert('O campo Data da Instalação é obrigatório para Validação OK');
          return;
        }
        if (!tratamentoData.numero_ordem || !tratamentoData.numero_ordem.trim()) {
          alert('O campo Número da Ordem é obrigatório para Validação OK');
          return;
        }
      }
    }

    // Validar campos obrigatórios para aguardando_aceite
    if (tratamentoData.status === 'aguardando_aceite') {
      if (!tratamentoData.simulacao || !tratamentoData.simulacao.trim()) {
        alert('O campo Simulação é obrigatório para o status Aguardando Aceite');
        return;
      }
      if (!tratamentoData.link_aceite || !tratamentoData.link_aceite.trim()) {
        alert('O campo Link de Aceite é obrigatório para o status Aguardando Aceite');
        return;
      }
    }

    // Validar campos obrigatórios para pendente_biometria
    if (tratamentoData.status === 'pendente_biometria') {
      if (!tratamentoData.numero_pedido_pap || !tratamentoData.numero_pedido_pap.trim()) {
        alert('O campo Número do Pedido PAP é obrigatório para o status Pendente Biometria');
        return;
      }
    }

    const statusComImagemObrigatoria = ['sem_viabilidade', 'cancelado'];
    if (statusComImagemObrigatoria.includes(tratamentoData.status)) {
      if (!tratamentoData.motivo_imagem || !tratamentoData.motivo_imagem.trim()) {
        alert('O campo Motivo é obrigatório para este status');
        return;
      }
      if (!imagemFile) {
        alert('A imagem de comprovação é obrigatória para este status');
        return;
      }
    }

    try {
      setUpdating(true);
      const novoStatus = tratamentoData.status;
      const dataInstalacao = (novoStatus === 'aguardando_instalacao' || novoStatus === 'validacao_ok') && tratamentoData.data_instalacao
        ? tratamentoData.data_instalacao
        : undefined;
      const numeroOrdem = (novoStatus === 'aguardando_instalacao' || novoStatus === 'validacao_ok') && tratamentoData.numero_ordem
        ? tratamentoData.numero_ordem
        : undefined;
      const dataConclusao = novoStatus === 'concluido' && tratamentoData.data_conclusao
        ? tratamentoData.data_conclusao
        : undefined;
      const simulacao = novoStatus === 'aguardando_aceite' ? tratamentoData.simulacao : undefined;
      const linkAceite = novoStatus === 'aguardando_aceite' ? tratamentoData.link_aceite : undefined;
      const numeroPedidoPap = novoStatus === 'pendente_biometria' ? tratamentoData.numero_pedido_pap : undefined;
      const motivoImagem = statusComImagemObrigatoria.includes(novoStatus)
        ? tratamentoData.motivo_imagem 
        : undefined;
      
      // Se tiver imagem para upload, fazer upload primeiro
      let imagemUrl = undefined;
      if (imagemFile && statusComImagemObrigatoria.includes(novoStatus)) {
        const formData = new FormData();
        formData.append('imagem', imagemFile);
        formData.append('motivo_imagem', motivoImagem);
        
        const uploadResponse = await salesService.uploadImagem(selectedVenda.id, formData);
        imagemUrl = uploadResponse.imagem_anexo;
      }
      
      await salesService.updateStatus(
        selectedVenda.id,
        novoStatus,
        tratamentoData.observacao_tratamento,
        dataInstalacao,
        numeroOrdem,
        dataConclusao,
        simulacao,
        linkAceite,
        imagemUrl,
        motivoImagem,
        numeroPedidoPap
      );
      
      alert('✅ Status atualizado com sucesso!');

      closeTratamento();
      // Voltar para a fila principal (pendente_bko) após finalizar o tratamento
      setFiltroStatus('pendente_bko');
      loadFila();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('❌ Erro ao atualizar status: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdating(false);
    }
  };

  const handleValidacaoOkConfirm = () => {
    setValidacaoOkModal('dados');
    setTratamentoData(prev => ({
      ...prev,
      data_instalacao: prev.data_instalacao || selectedVenda?.data_agendamento1 || getTodayBrasilia()
    }));
  };

  const handleValidacaoOkEditar = async () => {
    try {
      const vendaCompleta = await salesService.getById(selectedVenda.id);
      setEditandoValidacaoOk({
        ...vendaCompleta,
        observacao_venda: vendaCompleta?.observacao_venda || ''
      });
      setValidacaoOkModal(null);
    } catch (error) {
      console.error('Erro ao carregar venda para edição:', error);
      alert('Erro ao carregar venda para edição');
    }
  };

  const handleValidacaoOkEditSaved = () => {
    setEditandoValidacaoOk(null);
    closeTratamento();
    setFiltroStatus('pendente_bko');
    loadFila();
  };

  // Ordena a fila: prioridade_fila primeiro (retorno de estruturante),
  // depois por tempo de espera (quem está há mais tempo aguardando vem primeiro).
  // Vendas sem tempo definido ficam no fim do respectivo grupo.
  const vendasOrdenadas = [...vendas].sort((a, b) => {
    const pa = a.prioridade_fila ? 1 : 0;
    const pb = b.prioridade_fila ? 1 : 0;
    if (pa !== pb) return pb - pa;
    const ta = a.status_entered_at ? new Date(a.status_entered_at).getTime() : Infinity;
    const tb = b.status_entered_at ? new Date(b.status_entered_at).getTime() : Infinity;
    return ta - tb;
  });

  // "Próxima da fila": primeiro pendente_bko da lista ordenada
  // (priorizada mais antiga, ou a mais antiga sem prioridade se não houver priorizadas).
  const proximaVenda = vendasOrdenadas.find((v) => v.status === 'pendente_bko');
  const proximaVendaId = proximaVenda ? proximaVenda.id : null;

  return (
    <Layout>
      <div className="fila-page">
        {/* Toast de notificação visual (nova venda na fila) */}
        {visualNotification && (
          <div
            className="fila-notification-toast"
            role="alert"
            aria-live="polite"
          >
            <span className="fila-notification-toast-icon">🔔</span>
            <div className="fila-notification-toast-content">
              <strong>Nova{visualNotification.quantidade > 1 ? 's' : ''} venda{visualNotification.quantidade > 1 ? 's' : ''} na fila</strong>
              <p>
                {visualNotification.quantidade === 1
                  ? 'Uma nova venda entrou na fila de tratamento.'
                  : `${visualNotification.quantidade} novas vendas entraram na fila de tratamento.`}
              </p>
            </div>
            <button
              type="button"
              className="fila-notification-toast-close"
              onClick={() => {
                setVisualNotification(null);
                if (visualNotificationTimerRef.current) {
                  clearTimeout(visualNotificationTimerRef.current);
                  visualNotificationTimerRef.current = null;
                }
              }}
              aria-label="Fechar notificação"
            >
              ×
            </button>
          </div>
        )}
        <div className="page-header fila-page-header">
          <div className="fila-page-header-text">
            <h1>📋 Fila de Tratamento</h1>
            <p>Gerencie as vendas que precisam de tratamento</p>
          </div>
          {['admin', 'diretor'].includes(user?.role) && (
            <div className="fila-ver-tudo-actions">
              <button
                type="button"
                className={filaVisao?.manual ? 'btn-ver-tudo btn-ver-tudo-ativo' : 'btn-ver-tudo'}
                onClick={handleToggleVerTudo}
                disabled={togglingVerTudo}
                title={
                  filaVisao?.manual
                    ? 'Desativa o modo manual Ver tudo (entre 19:00 e 00:00 a fila continua completa pelo horário)'
                    : 'Ativa visão completa da fila para todos os BKO até 00:00 (São Paulo)'
                }
              >
                {togglingVerTudo
                  ? '...'
                  : filaVisao?.manual
                    ? 'Desativar ver tudo'
                    : 'Ver tudo'}
              </button>
            </div>
          )}
        </div>

        {filaVisao?.ativa && (
          <div className="fila-visao-banner" role="status">
            <span className="fila-visao-banner-icon">👁️</span>
            <div className="fila-visao-banner-text">
              <strong>Fila completa liberada</strong>
              <span>{getFilaVisaoMensagem()}</span>
            </div>
          </div>
        )}

        <div className="filters">
          <div className="filter-group">
            <label>Buscar:</label>
            <input 
              type="text" 
              placeholder="Buscar por cliente, documento, telefone, vendedor..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>Vendedor:</label>
            <select 
              value={filtroVendedor} 
              onChange={(e) => setFiltroVendedor(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              {vendedores.map(vendedor => (
                <option key={vendedor.id} value={vendedor.id}>{vendedor.nome}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Tipo:</label>
            <select
              value={filtroTipoCliente}
              onChange={(e) => {
                const value = e.target.value;
                setFiltroTipoCliente(value);
                if (value === 'cnpj') setFiltroMarca('');
              }}
              className="filter-select"
            >
              <option value="">Todos</option>
              <option value="cpf">CPF</option>
              <option value="cnpj">CNPJ</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Marca:</label>
            <select
              value={filtroMarca}
              onChange={(e) => setFiltroMarca(e.target.value)}
              className="filter-select"
              disabled={filtroTipoCliente === 'cnpj'}
            >
              <option value="">Todas</option>
              <option value="extk">EXTK</option>
              <option value="onda">ONDA TELECOM</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={filtroStatus} 
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="filter-select"
            >
              <option value="pendente_bko">Pendente BKO</option>
              <option value="tratando">Tratando</option>
              <option value="pendente_vendedor">Pendente Vendedor</option>
              <option value="pendente_sistemica">Pendente Sistêmica</option>
              <option value="pendente_biometria">Pendente Biometria</option>
              <option value="pendente_auditoria">Pendente Auditoria</option>
              <option value="pendente_imput">Pendente Imput</option>
              <option value="tramitacao_manual">Tramitação Manual</option>
              <option value="critica">Crítica</option>
              <option value="mesa_de_credito">Mesa de Crédito</option>
              <option value="aguardando_whatsapp">Aguardando WhatsApp</option>
              <option value="aguardando_dados_sfa">Aguardando dados SFA</option>
              <option value="validacao_ok">Validação OK</option>
              <option value="aguardando_instalacao">Aguardando Instalação</option>
              <option value="aguardando_aceite">Aguardando Aceite</option>
              <option value="instalacao_cancelada">Instalação cancelada</option>
              <option value="sem_viabilidade">Sem Viabilidade</option>
              <option value="aguardando_estruturante">Aguardando Estruturante</option>
              <option value="aguardando_can_ou_reag">Aguardando CAN ou REAG</option>
              <option value="cancelamento_comercial">Cancelamento comercial</option>
              <option value="cancelamento_tecnico">Cancelamento técnico</option>
              <option value="all">Todos (Pendentes)</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading">Carregando fila...</div>
        ) : vendas.length === 0 ? (
          <div className="empty-state">
            <p>📭 Nenhuma venda na fila de tratamento</p>
          </div>
        ) : (
          <>
          <div className="fila-legenda">
            <span className="fila-legenda-titulo">⏱️ Tempo na fila:</span>
            <span className="fila-legenda-item"><span className="fila-tempo-dot" style={{ background: '#51cf66' }} /> No prazo (&lt; 30min)</span>
            <span className="fila-legenda-item"><span className="fila-tempo-dot" style={{ background: '#ffd43b' }} /> Atenção (30min–2h)</span>
            <span className="fila-legenda-item"><span className="fila-tempo-dot" style={{ background: '#ffa500' }} /> Urgente (2h–1d)</span>
            <span className="fila-legenda-item"><span className="fila-tempo-dot" style={{ background: '#ff6b6b' }} /> Crítico (1d+)</span>
            <span className="fila-legenda-item fila-legenda-proxima">👉 PRÓXIMA = pegue esta em seguida</span>
            <span className="fila-legenda-item fila-legenda-prioridade">⚡ PRIORIDADE = voltou de Aguardando Estruturante</span>
          </div>
          <div className="table-container">
            <table className="fila-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Tempo na Fila</th>
                  <th>Cliente</th>
                  <th>Documento</th>
                  <th>Telefone</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Vendedor</th>
                  <th>BKO Atribuído</th>
                  <th>Operadora</th>
                  <th>Cadastro</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {vendasOrdenadas.map((venda) => (
                  <tr
                    key={venda.id}
                    className={[
                      venda.tipo_cliente === 'cnpj' ? 'fila-row-cnpj' : '',
                      venda.id === proximaVendaId ? 'fila-row-proxima' : '',
                      venda.prioridade_fila && venda.id !== proximaVendaId ? 'fila-row-prioridade' : ''
                    ].filter(Boolean).join(' ') || undefined}
                  >
                    <td>
                      #{venda.id}
                      {venda.id === proximaVendaId && (
                        <span
                          className="fila-badge-proxima"
                          title={
                            venda.prioridade_fila
                              ? 'Prioridade: voltou de Aguardando Estruturante — pegue esta em seguida'
                              : 'Venda que está há mais tempo aguardando — pegue esta em seguida'
                          }
                        >
                          👉 PRÓXIMA
                        </span>
                      )}
                      {venda.prioridade_fila && venda.id !== proximaVendaId && (
                        <span
                          className="fila-badge-prioridade"
                          title="Voltou de Aguardando Estruturante — prioridade na fila"
                        >
                          ⚡ PRIORIDADE
                        </span>
                      )}
                    </td>
                    <td>
                      {venda.status_entered_at ? (
                        (() => {
                          const { nivel, cor } = getNivelEspera(venda.status_entered_at);
                          return (
                            <span
                              className={`fila-tempo-badge fila-tempo-${nivel}`}
                              style={{ color: cor, borderColor: cor }}
                              title={`Entrou na fila em ${formatDateTime(venda.status_entered_at)}`}
                            >
                              <span className="fila-tempo-dot" style={{ background: cor }} />
                              {calcularTempoEspera(venda.status_entered_at)}
                            </span>
                          );
                        })()
                      ) : (
                        <span style={{ color: 'var(--texto-secundario)', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                    <td>{venda.nome_cliente}</td>
                    <td>{venda.documento}</td>
                    <td>{venda.telefone}</td>
                    <td>
                      R$ {parseFloat(venda.valor_total).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td>
                      {(venda.status === 'aguardando_instalacao' || venda.status === 'validacao_ok') && venda.data_instalacao && (
                        <small style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--texto-secundario)' }}>
                          {formatDateOnly(venda.data_instalacao)}
                          {venda.numero_ordem ? ` · Ordem: ${venda.numero_ordem}` : ''}
                        </small>
                      )}
                      <span className={`status-badge ${getStatusClass(venda.status)}`}>
                        {getStatusText(venda.status)}
                      </span>
                    </td>
                    <td>{venda.vendedor?.nome || '-'}</td>
                    <td>
                      {venda.tratador?.nome ? (
                        <span style={{ color: 'var(--azul-dvs)', fontWeight: '600' }}>
                          {venda.tratador.nome}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--texto-secundario)', fontStyle: 'italic' }}>
                          Sem atribuição
                        </span>
                      )}
                    </td>
                    <td>
                      {venda.operadora_selecionada ? (
                        <span style={{ fontWeight: '600' }}>
                          {venda.operadora_selecionada.charAt(0).toUpperCase() + 
                           venda.operadora_selecionada.slice(1).toLowerCase()}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--texto-secundario)' }}>-</span>
                      )}
                    </td>
                    <td>{formatDate(venda.created_at)}</td>
                    <td>
                      <div className="fila-acoes">
                        {['admin', 'diretor'].includes(user?.role) && (
                          <button
                            type="button"
                            onClick={() => visualizarVenda(venda)}
                            className="btn-action-icon"
                            title="Visualizar ficha"
                            aria-label="Visualizar ficha"
                          >
                            👁️
                          </button>
                        )}
                        {vendaSomenteLeitura(venda) ? (
                          <button
                            type="button"
                            onClick={() => visualizarVenda(venda)}
                            className="btn-action"
                            title="Venda em tratamento. Somente visualização."
                          >
                            👁️ Ver
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openTratamento(venda)}
                            className="btn-action"
                          >
                            🔧 Tratar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Modal de tratamento */}
        {selectedVenda && (
          <ModalPortal>
          <div className="modal-overlay" onClick={closeTratamento}>
            <div className="modal-content tratamento-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{somenteVisualizacao ? '👁️ Visualizar' : '🔧 Tratar'} Venda #{selectedVenda.id}</h2>
                <div className="modal-header-actions">
                  {selectedVenda.operadora_selecionada && 
                   selectedVenda.operadora_selecionada.toLowerCase() === 'claro' && (
                    <button
                      type="button"
                      onClick={downloadExcelClaro}
                      className="btn-primary"
                      style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', background: '#28a745' }}
                      title="Baixar checklist em Excel"
                    >
                      📥 Download Excel
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={copiarFichaCadastro}
                    className="btn-secondary"
                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    📋 Copiar tudo
                  </button>
                  <button
                    type="button"
                    onClick={copiarNeo}
                    className="btn-secondary"
                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    📄 Copiar Neo
                  </button>
                  <button
                    type="button"
                    onClick={copiarNeoCPF}
                    className="btn-secondary"
                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                  >
                    📋 Copiar Neo CPF
                  </button>
                  <button onClick={closeTratamento} className="btn-close">✕</button>
                </div>
              </div>
              
              <div className="modal-body">
                {somenteVisualizacao && user?.role === 'backoffice' && selectedVenda.status === 'tratando' && (
                  <div className="observacao-box" style={{ marginBottom: '1rem', padding: '0.85rem 1rem', background: 'var(--cinza-escuro-2, #2a2a2a)', borderRadius: '8px' }}>
                    Esta venda está em tratamento{selectedVenda.tratador?.nome ? ` por ${selectedVenda.tratador.nome}` : ''}. Você pode apenas visualizar as informações.
                  </div>
                )}
                {/* Observações internas (BKO/Diretoria) - apenas admin, diretor, supervisor_bko */}
                {['admin', 'diretor', 'supervisor_bko'].includes(user?.role) && (
                  <div className="observacao-box" style={{ marginBottom: '1.5rem', borderLeftColor: 'var(--azul-dvs, #0066cc)', padding: '1rem', background: 'var(--cinza-escuro-2, #2a2a2a)', borderRadius: '8px' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>📋 Observações internas (BKO/Diretoria)</h3>
                    {selectedVenda.observacoes_supervisao && parseObservacoesSupervisao(selectedVenda.observacoes_supervisao).length > 0 ? (
                      <div style={{ maxHeight: '180px', overflowY: 'auto', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                        {parseObservacoesSupervisao(selectedVenda.observacoes_supervisao).map((o, idx) => (
                          <div key={idx} style={{ marginBottom: '0.5rem', padding: '0.35rem 0', borderBottom: '1px solid var(--border-color, #444)' }}>
                            {o.dataHora ? <><span style={{ color: 'var(--azul-dvs, #0066cc)', fontWeight: 600 }}>[{o.dataHora}] {o.usuario}:</span> {o.texto}</> : o.texto}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: 'var(--text-muted, #888)', marginBottom: '0.75rem', fontSize: '0.9rem' }}>Nenhuma observação ainda.</p>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <textarea
                        value={novaObservacaoSupervisao}
                        onChange={(e) => setNovaObservacaoSupervisao(e.target.value)}
                        placeholder="Nova observação..."
                        rows={2}
                        style={{ flex: 1, resize: 'vertical', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                      />
                      <button
                        type="button"
                        onClick={handleAddObservacaoSupervisao}
                        disabled={!novaObservacaoSupervisao.trim() || addingObservacaoSupervisao}
                        className="btn-primary"
                        style={{ alignSelf: 'flex-end' }}
                      >
                        {addingObservacaoSupervisao ? '...' : 'Adicionar'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Informações do Cliente */}
                <div className="venda-info">
                  <h3>👤 Dados do Cliente</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Tipo de Cliente:</strong>
                      <span>{selectedVenda.tipo_cliente === 'cpf' ? 'CPF' : 'CNPJ'}</span>
                    </div>
                    {selectedVenda.tipo_cliente === 'cpf' && (
                      <div className="info-item">
                        <strong>Marca:</strong>
                        <span>{formatMarca(selectedVenda.marca)}</span>
                      </div>
                    )}
                    <div className="info-item">
                      <strong>Documento Principal:</strong>
                      <span>{selectedVenda.tipo_cliente === 'cnpj' ? (selectedVenda.documento ? selectedVenda.documento.replace(/\D/g, '') : '-') : formatDocumento(selectedVenda.documento)}</span>
                    </div>
                    <div className="info-item">
                      <strong>Nome do Cliente:</strong>
                      <span>{selectedVenda.nome_cliente || '-'}</span>
                    </div>
                    {selectedVenda.tipo_cliente === 'cnpj' && (
                      <>
                        <div className="info-item">
                          <strong>Razão Social:</strong>
                          <span>{selectedVenda.razao_social || '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>Estado do CNPJ:</strong>
                          <span>{selectedVenda.estado_cnpj || '-'}</span>
                        </div>
                        {selectedVenda.nome_representante && (
                          <div className="info-item">
                            <strong>Nome do Representante:</strong>
                            <span>{selectedVenda.nome_representante}</span>
                          </div>
                        )}
                        <div className="info-item">
                          <strong>CPF do Representante:</strong>
                          <span>{selectedVenda.cpf ? selectedVenda.cpf.replace(/\D/g, '') : '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>RG do Representante:</strong>
                          <span>{selectedVenda.rg || '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>Nome da Mãe do Representante:</strong>
                          <span>{selectedVenda.nome_mae || '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>Data de Nascimento do Representante:</strong>
                          <span>{selectedVenda.data_nascimento ? formatDateOnly(selectedVenda.data_nascimento) : '-'}</span>
                        </div>
                      </>
                    )}
                    {selectedVenda.tipo_cliente === 'cpf' && (
                      <>
                        <div className="info-item">
                          <strong>CPF:</strong>
                          <span>{selectedVenda.cpf ? formatCPF(selectedVenda.cpf) : (selectedVenda.documento ? formatCPF(selectedVenda.documento) : '-')}</span>
                        </div>
                        <div className="info-item">
                          <strong>RG:</strong>
                          <span>{selectedVenda.rg || '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>Nome da Mãe:</strong>
                          <span>{selectedVenda.nome_mae || '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>Data de Nascimento:</strong>
                          <span>{selectedVenda.data_nascimento ? formatDateOnly(selectedVenda.data_nascimento) : '-'}</span>
                        </div>
                      </>
                    )}
                    {renderizarCampoContato('Email', selectedVenda.email ? selectedVenda.email.toLowerCase() : '-', 'email', selectedVenda.email)}
                    {renderizarCampoContato('Telefone 1', selectedVenda.tipo_cliente === 'cnpj' ? (selectedVenda.telefone ? selectedVenda.telefone.replace(/\D/g, '') : '-') : (selectedVenda.telefone ? formatPhone(selectedVenda.telefone) : '-'), 'telefone', selectedVenda.telefone)}
                    {renderizarCampoContato('Telefone 2', selectedVenda.tipo_cliente === 'cnpj' ? (selectedVenda.telefone2 ? selectedVenda.telefone2.replace(/\D/g, '') : '-') : (selectedVenda.telefone2 ? formatPhone(selectedVenda.telefone2) : '-'), 'telefone', selectedVenda.telefone2)}
                  </div>
                </div>

                {/* Informações de Endereço */}
                <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                  <h3>📍 Endereço</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>CEP:</strong>
                      <span>{selectedVenda.cep || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Logradouro:</strong>
                      <span>{selectedVenda.logradouro || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Número:</strong>
                      <span>{selectedVenda.numero || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Complemento 1:</strong>
                      <span>{selectedVenda.complemento1 || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Complemento 2:</strong>
                      <span>{selectedVenda.complemento2 || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Complemento 3:</strong>
                      <span>{selectedVenda.complemento3 || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Bairro:</strong>
                      <span>{selectedVenda.bairro || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Cidade:</strong>
                      <span>{selectedVenda.cidade || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Estado:</strong>
                      <span>{selectedVenda.estado || '-'}</span>
                    </div>
                    {selectedVenda.estado_completo && (
                      <div className="info-item">
                        <strong>Estado Completo:</strong>
                        <span>{selectedVenda.estado_completo}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Informações de Agendamento */}
                <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                  <h3>📅 Agendamento</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Data de Agendamento 1:</strong>
                      <span>{selectedVenda.data_agendamento1 ? formatDateOnly(selectedVenda.data_agendamento1) : '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Período de Agendamento 1:</strong>
                      <span>
                        {selectedVenda.periodo_agendamento1 === 'manha' ? 'Manhã' : 
                         selectedVenda.periodo_agendamento1 === 'tarde' ? 'Tarde' : 
                         selectedVenda.periodo_agendamento1 === 'noite' ? 'Noite' : '-'}
                      </span>
                    </div>
                    {selectedVenda.data_agendamento2 && (
                      <>
                        <div className="info-item">
                          <strong>Data de Agendamento 2:</strong>
                          <span>{formatDateOnly(selectedVenda.data_agendamento2)}</span>
                        </div>
                        <div className="info-item">
                          <strong>Período de Agendamento 2:</strong>
                          <span>
                            {selectedVenda.periodo_agendamento2 === 'manha' ? 'Manhã' : 
                             selectedVenda.periodo_agendamento2 === 'tarde' ? 'Tarde' : 
                             selectedVenda.periodo_agendamento2 === 'noite' ? 'Noite' : '-'}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="info-item">
                      <strong>Aceita Antecipação:</strong>
                      <span>{selectedVenda.aceita_antecipacao ? '✅ Sim' : '❌ Não'}</span>
                    </div>
                    {selectedVenda.observacao_instalacao && (
                      <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                        <strong>Observação de Instalação:</strong>
                        <span>{selectedVenda.observacao_instalacao}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Informações Financeiras */}
                <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                  <h3>💰 Informações Financeiras</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Valor Total:</strong>
                      <span className="value-highlight">
                        R$ {parseFloat(selectedVenda.valor_total || 0).toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </span>
                    </div>
                    <div className="info-item">
                      <strong>Data de Vencimento:</strong>
                      <span>Dia {selectedVenda.data_vencimento}</span>
                    </div>
                  </div>
                </div>

                {/* Simulação e Link de Aceite - mostrar sempre que existir */}
                {(selectedVenda.simulacao || selectedVenda.link_aceite) && (
                  <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                    <h3>📄 Simulação e Aceite</h3>
                    <div className="info-grid">
                      {selectedVenda.simulacao && (
                        <div className="info-item">
                          <strong>Número da Simulação:</strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                            <span style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: '600', color: 'var(--azul-dvs)' }}>
                              {selectedVenda.simulacao}
                            </span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(selectedVenda.simulacao);
                                alert('Número da simulação copiado!');
                              }}
                              className="btn-copy"
                              title="Copiar número da simulação"
                            >
                              📋 Copiar
                            </button>
                          </div>
                        </div>
                      )}
                      {selectedVenda.link_aceite && (
                        <div className="info-item" style={{ gridColumn: selectedVenda.simulacao ? '1 / -1' : '1' }}>
                          <strong>Link de Aceite:</strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                            <a 
                              href={selectedVenda.link_aceite} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: 'var(--azul-dvs)', 
                                wordBreak: 'break-all',
                                fontSize: '0.9rem',
                                textDecoration: 'underline'
                              }}
                            >
                              {selectedVenda.link_aceite}
                            </a>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(selectedVenda.link_aceite);
                                alert('Link copiado para a área de transferência!');
                              }}
                              className="btn-copy"
                              title="Copiar link"
                            >
                              📋 Copiar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Número do Pedido PAP - mostrar sempre que existir */}
                {selectedVenda.numero_pedido_pap && (
                  <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                    <h3>📋 Pedido PAP</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <strong>Número do Pedido PAP:</strong>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                          <span style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: '600', color: 'var(--azul-dvs)' }}>
                            {selectedVenda.numero_pedido_pap}
                          </span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(selectedVenda.numero_pedido_pap);
                              alert('Número do pedido PAP copiado!');
                            }}
                            className="btn-copy"
                            title="Copiar número do pedido PAP"
                          >
                            📋 Copiar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Dados PAP do Vendedor - mostrar para vendas CPF quando existir */}
                {selectedVenda.tipo_cliente === 'cpf' && selectedVenda.vendedor_codigo_pap && (
                  <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                    <h3>📋 Dados PAP do Vendedor</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <strong>Código PAP:</strong>
                        <span style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: '600', color: 'var(--azul-dvs)', marginLeft: '0.5rem' }}>
                          {selectedVenda.vendedor_codigo_pap}
                        </span>
                      </div>
                      <div className="info-item">
                        <strong>Login PAP:</strong>
                        <span style={{ marginLeft: '0.5rem' }}>
                          {selectedVenda.vendedor_login_pap || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Informações da Venda */}
                <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                  <h3>ℹ️ Informações da Venda</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Vendedor:</strong>
                      <span>{selectedVenda.vendedor?.nome || '-'}</span>
                    </div>
                    <div className="info-item">
                      <strong>Status:</strong>
                      {selectedVenda.status === 'aguardando_instalacao' && selectedVenda.data_instalacao && (
                        <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                          Data instalação: {formatDateOnly(selectedVenda.data_instalacao)}
                        </span>
                      )}
                      {selectedVenda.status === 'aguardando_instalacao' && selectedVenda.numero_ordem && (
                        <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                          Ordem: {selectedVenda.numero_ordem}
                        </span>
                      )}
                      {selectedVenda.status === 'concluido' && selectedVenda.data_conclusao && (
                        <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                          Concluído em: {formatDateOnly(selectedVenda.data_conclusao)}
                        </span>
                      )}
                      <span className={`status-badge ${getStatusClass(selectedVenda.status)}`}>
                        {getStatusText(selectedVenda.status)}
                      </span>
                    </div>
                    <div className="info-item">
                      <strong>Cadastrado em:</strong>
                      <span>{formatDateTime(selectedVenda.created_at)}</span>
                    </div>
                    {selectedVenda.tratado_por && (
                      <>
                        <div className="info-item">
                          <strong>Tratado por:</strong>
                          <span>{selectedVenda.tratador?.nome || '-'}</span>
                        </div>
                        <div className="info-item">
                          <strong>Tratado em:</strong>
                          <span>{selectedVenda.tratado_em ? formatDateTime(selectedVenda.tratado_em) : '-'}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Informações de Viabilidade */}
                {selectedVenda.operadora_selecionada && (
                  <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                    <h3>📡 Informações de Viabilidade</h3>
                    <div className="info-grid">
                      <div className="info-item">
                        <strong>Operadora Selecionada:</strong>
                        <span style={{ fontWeight: '600' }}>
                          {selectedVenda.operadora_selecionada.charAt(0).toUpperCase() + selectedVenda.operadora_selecionada.slice(1)}
                        </span>
                      </div>
                      {selectedVenda.operadora_viabilidade_data && (
                        <div className="info-item">
                          <strong>Data da Consulta:</strong>
                          <span>{formatDateTime(selectedVenda.operadora_viabilidade_data)}</span>
                        </div>
                      )}
                      {selectedVenda.operadora_viabilidade_json && (
                        <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                          <strong>Status de Viabilidade:</strong>
                          <span>
                            {(() => {
                              try {
                                let viabilidade = selectedVenda.operadora_viabilidade_json;
                                
                                // Se for string, fazer parse
                                if (typeof viabilidade === 'string') {
                                  try {
                                    viabilidade = JSON.parse(viabilidade);
                                    // Se o resultado ainda for string (double-encoded), fazer parse novamente
                                    if (typeof viabilidade === 'string') {
                                      viabilidade = JSON.parse(viabilidade);
                                    }
                                  } catch (parseError) {
                                    console.error('Erro ao fazer parse do JSON:', parseError);
                                    return <span style={{ color: '#dc3545' }}>Erro ao processar JSON de viabilidade</span>;
                                  }
                                }
                                
                                console.log('📋 Viabilidade JSON parseado (Tratar Venda):', viabilidade);
                                
                                // Se viabilidade é null ou undefined
                                if (!viabilidade) {
                                  return <span style={{ color: '#868e96' }}>Dados de viabilidade não disponíveis</span>;
                                }
                                
                                // Verificar se tem operadoras diretamente ou dentro de uma propriedade
                                let operadoras = null;
                                if (viabilidade.operadoras && typeof viabilidade.operadoras === 'object') {
                                  operadoras = viabilidade.operadoras;
                                } else if (viabilidade.data && viabilidade.data.operadoras) {
                                  operadoras = viabilidade.data.operadoras;
                                } else if (typeof viabilidade === 'object' && !Array.isArray(viabilidade)) {
                                  const keys = Object.keys(viabilidade);
                                  const hasOperadoraStructure = keys.some(key => {
                                    const val = viabilidade[key];
                                    return val && typeof val === 'object' && ('coverage' in val);
                                  });
                                  if (hasOperadoraStructure) {
                                    operadoras = viabilidade;
                                  }
                                }
                                
                                if (!operadoras || (typeof operadoras === 'object' && Object.keys(operadoras).length === 0)) {
                                  console.warn('Viabilidade sem operadoras válidas:', viabilidade);
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                      <span style={{ color: '#868e96' }}>Dados de viabilidade não contêm operadoras</span>
                                      <details style={{ fontSize: '0.85rem', color: '#868e96' }}>
                                        <summary style={{ cursor: 'pointer' }}>Ver estrutura do JSON</summary>
                                        <pre style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#1e1e1e', borderRadius: '4px', overflow: 'auto', maxHeight: '200px' }}>
                                          {JSON.stringify(viabilidade, null, 2)}
                                        </pre>
                                      </details>
                                    </div>
                                  );
                                }

                                // Normalizar nome da operadora (case-insensitive, remover espaços extras)
                                const operadoraSelecionadaNormalizada = selectedVenda.operadora_selecionada 
                                  ? selectedVenda.operadora_selecionada.toLowerCase().trim()
                                  : null;

                                // Procurar operadora no JSON (case-insensitive)
                                let operadora = null;
                                if (operadoraSelecionadaNormalizada && operadoras) {
                                  // Tentar encontrar pelo nome exato primeiro
                                  operadora = operadoras[selectedVenda.operadora_selecionada];
                                  
                                  // Se não encontrar, procurar case-insensitive
                                  if (!operadora) {
                                    const operadorasKeys = Object.keys(operadoras);
                                    const operadoraEncontrada = operadorasKeys.find(key => 
                                      key.toLowerCase().trim() === operadoraSelecionadaNormalizada
                                    );
                                    if (operadoraEncontrada) {
                                      operadora = operadoras[operadoraEncontrada];
                                    }
                                  }
                                }

                                if (!operadora) {
                                  console.warn('Operadora não encontrada na viabilidade:', {
                                    operadoraSelecionada: selectedVenda.operadora_selecionada,
                                    operadoras: Object.keys(operadoras)
                                  });
                                  return <span style={{ color: '#868e96' }}>Operadora não encontrada nos dados de viabilidade</span>;
                                }
                                
                                const hasCoverage = operadora.coverage === true;
                                console.log('✅ Viabilidade da operadora:', { operadora, hasCoverage });
                                
                                return (
                                  <span className={`status-badge ${hasCoverage ? 'status-ativo' : 'status-inativo'}`} style={{ 
                                    background: hasCoverage ? '#4caf50' : '#ff9800',
                                    color: '#000 !important',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '4px',
                                    fontWeight: '600'
                                  }}>
                                    {hasCoverage ? '✓ Viabilidade Confirmada' : '⚠ Sem Viabilidade Confirmada - BKO consultará manualmente'}
                                  </span>
                                );
                              } catch (e) {
                                console.error('Erro ao processar viabilidade:', e);
                                return <span style={{ color: '#dc3545' }}>Erro ao processar dados de viabilidade</span>;
                              }
                            })()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Envio de Aceite */}
                <div className="venda-info" style={{ marginTop: '1.5rem' }}>
                  <h3>📧 Envio de Aceite</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <strong>Operadora:</strong>
                      <span>
                        {selectedVenda.envio_aceite ? (() => {
                          const parts = selectedVenda.envio_aceite.split(':');
                          return parts.length > 0 
                            ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
                            : '-';
                        })() : 'Não informado'}
                      </span>
                    </div>
                    <div className="info-item">
                      <strong>Meios de Envio:</strong>
                      <span>
                        {selectedVenda.envio_aceite ? (() => {
                          const parts = selectedVenda.envio_aceite.split(':');
                          if (parts.length < 2) return '-';
                          
                          const meios = parts[1].split(',');
                          return <MeiosEnvioList meios={meios} />;
                        })() : 'Não informado'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Produtos PF (CPF) */}
                {selectedVenda.tipo_cliente === 'cpf' && parseProdutosSelecionados(selectedVenda.produtos_selecionados_json).length > 0 && (
                  <div className="observacao-box" style={{ marginTop: '1.5rem' }}>
                    <strong>📦 Produtos PF (NEO):</strong>
                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                      {parseProdutosSelecionados(selectedVenda.produtos_selecionados_json).map((p) => (
                        <li key={p.catalogo_plano_id || p.nome_neo}>
                          {p.nome_neo} — Qtd {p.quantidade || 1} — R$ {Number(p.valor_negociado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Observações */}
                {selectedVenda.observacao_venda && (
                  <div className="observacao-box" style={{ marginTop: '1.5rem' }}>
                    <strong>📝 Observação da Venda:</strong>
                    <p>{selectedVenda.observacao_venda}</p>
                  </div>
                )}

                {/* Histórico de Observações de Tratamento (somente leitura) */}
                {selectedVenda.observacao_tratamento && (
                  <div style={{ marginTop: '1rem' }}>
                    <strong style={{ display: 'block', marginBottom: '0.75rem' }}>📋 Histórico de Tratamento (somente leitura):</strong>
                    <div style={{ 
                      maxHeight: '250px', 
                      overflowY: 'auto',
                      background: 'var(--cinza-escuro-2)',
                      borderRadius: '8px',
                      padding: '0.75rem'
                    }}>
                      {parseObservacoesTratamento(selectedVenda.observacao_tratamento).map((obs, index) => (
                        <div
                          key={index}
                          style={{
                            marginBottom: '0.75rem',
                            padding: '0.75rem',
                            background: 'var(--bg-primary)',
                            borderRadius: '6px',
                            borderLeft: obs.tipo === 'mudanca_status' ? '4px solid var(--azul-dvs)' : '4px solid #ff9800'
                          }}
                        >
                          {obs.tipo === 'mudanca_status' ? (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <span style={{ fontSize: '1rem' }}>🔄</span>
                                <span style={{ fontWeight: '600', color: 'var(--azul-dvs)', fontSize: '0.9rem' }}>Mudança de Status</span>
                                {obs.dataHora && (
                                  <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--texto-secundario)' }}>
                                    {obs.dataHora}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <span style={{ 
                                  padding: '0.3rem 0.6rem', 
                                  background: 'rgba(255, 152, 0, 0.2)', 
                                  borderRadius: '4px', 
                                  fontSize: '0.8rem',
                                  color: '#ff9800'
                                }}>
                                  {obs.statusAnterior}
                                </span>
                                <span style={{ color: 'var(--texto-secundario)' }}>→</span>
                                <span style={{ 
                                  padding: '0.3rem 0.6rem', 
                                  background: 'rgba(6, 102, 255, 0.2)', 
                                  borderRadius: '4px', 
                                  fontSize: '0.8rem',
                                  color: 'var(--azul-dvs)',
                                  fontWeight: '600'
                                }}>
                                  {obs.statusNovo}
                                </span>
                              </div>
                              {obs.usuario && (
                                <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--texto-secundario)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <span>👤</span>
                                  <span>Por: <strong>{obs.usuario}</strong></span>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <span style={{ fontSize: '1rem' }}>💬</span>
                                <span style={{ fontWeight: '600', color: '#ff9800', fontSize: '0.9rem' }}>Observação</span>
                                {obs.dataHora && (
                                  <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--texto-secundario)' }}>
                                    {obs.dataHora}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.9rem', color: 'var(--texto-primario)', lineHeight: '1.5' }}>
                                {obs.texto}
                              </div>
                              {obs.usuario && (
                                <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--texto-secundario)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <span>👤</span>
                                  <span>Por: <strong>{obs.usuario}</strong></span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                      {parseObservacoesTratamento(selectedVenda.observacao_tratamento).length === 0 && (
                        <div style={{ textAlign: 'center', color: 'var(--texto-secundario)', padding: '1rem' }}>
                          Nenhum registro encontrado
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Documentos Anexos */}
                {selectedVenda.documento_anexo && (
                  <div className="observacao-box" style={{ marginTop: '1rem', borderLeftColor: '#ff9800' }}>
                    <strong>📎 Documentos Anexos:</strong>
                    <div style={{ marginTop: '0.75rem' }}>
                      {(() => {
                        // Tentar fazer parse do JSON (múltiplos arquivos) ou usar como string (arquivo único - compatibilidade)
                        let documentos = [];
                        try {
                          const parsed = JSON.parse(selectedVenda.documento_anexo);
                          documentos = Array.isArray(parsed) ? parsed : [parsed];
                        } catch (e) {
                          // Se não for JSON, tratar como string única (compatibilidade com dados antigos)
                          documentos = [selectedVenda.documento_anexo];
                        }
                        
                        // Função para construir URL correta do documento
                        const getDocumentoUrl = (documentoPath) => {
                          // Se o caminho já começa com http, retornar como está
                          if (documentoPath.startsWith('http://') || documentoPath.startsWith('https://')) {
                            return documentoPath;
                          }
                          
                          // Usar a mesma URL base da API configurada no axios
                          let baseUrl = getApiBaseUrl();
                          
                          // Remover /api do final se existir (os uploads são servidos na raiz do servidor)
                          baseUrl = baseUrl.replace(/\/api\/?$/, '');
                          
                          // Remover barra final se existir
                          baseUrl = baseUrl.replace(/\/$/, '');
                          
                          // Garantir que o caminho tenha o formato correto
                          // Se não começar com "uploads/", adicionar
                          let path = documentoPath;
                          if (!path.startsWith('uploads/') && !path.startsWith('/uploads/')) {
                            // Se tiver apenas o nome do arquivo, adicionar o caminho completo
                            if (!path.includes('/')) {
                              path = `uploads/documentos/${path}`;
                            }
                          }
                          
                          // Garantir que o caminho comece com /
                          path = path.startsWith('/') ? path : `/${path}`;
                          
                          const fullUrl = `${baseUrl}${path}`;
                          console.log('📂 Construindo URL do documento:', {
                            documentoPath,
                            baseUrl,
                            path,
                            fullUrl
                          });
                          return fullUrl;
                        };
                        
                        return (
                          <>
                            {documentos.map((documentoPath, index) => {
                              const documentoUrl = getDocumentoUrl(documentoPath);
                              const isImage = /\.(jpg|jpeg|png)$/i.test(documentoPath);
                              const isPdf = /\.pdf$/i.test(documentoPath);
                              const fileName = documentoPath.split('/').pop();
                              
                              // Função para fazer download
                              const handleDownload = async (e) => {
                                e.preventDefault();
                                console.log('📥 Tentando baixar:', documentoUrl);
                                
                                try {
                                  // Tentar usar fetch primeiro
                                  const response = await fetch(documentoUrl, {
                                    method: 'GET',
                                    headers: {
                                      'Accept': '*/*'
                                    }
                                  });
                                  
                                  if (!response.ok) {
                                    console.error('❌ Erro na resposta:', response.status, response.statusText);
                                    throw new Error(`Arquivo não encontrado (${response.status})`);
                                  }
                                  
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = fileName;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(url);
                                  console.log('✅ Download iniciado com sucesso');
                                } catch (error) {
                                  console.error('❌ Erro ao baixar arquivo:', error);
                                  // Se fetch falhar, tentar abrir em nova aba como fallback
                                  alert(`⚠️ Não foi possível fazer o download automático. Abrindo em nova aba...\n\nErro: ${error.message}`);
                                  window.open(documentoUrl, '_blank');
                                }
                              };
                              
                              return (
                                <div key={index} style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(42, 42, 42, 0.3)', borderRadius: '8px' }}>
                                  <div style={{ marginBottom: '0.5rem', fontWeight: '600', color: 'var(--texto-primario)' }}>
                                    📄 Documento {documentos.length > 1 ? `${index + 1}` : ''}: {fileName}
                                  </div>
                                  {isImage && (
                                    <div style={{ marginBottom: '0.75rem', position: 'relative' }}>
                                      <img 
                                        src={documentoUrl}
                                        alt={`Documento anexo ${index + 1}`}
                                        style={{ 
                                          maxWidth: '100%', 
                                          maxHeight: '500px', 
                                          border: '1px solid var(--border-color)',
                                          borderRadius: '8px',
                                          cursor: 'pointer',
                                          objectFit: 'contain',
                                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                          display: 'block'
                                        }}
                                        onClick={() => window.open(documentoUrl, '_blank')}
                                        onError={(e) => {
                                          // Se a imagem falhar ao carregar, mostrar mensagem
                                          const img = e.target;
                                          img.style.display = 'none';
                                          const parent = img.parentNode;
                                          // Verificar se já existe mensagem de erro
                                          if (!parent.querySelector('.image-error-message')) {
                                            const errorDiv = document.createElement('div');
                                            errorDiv.className = 'image-error-message';
                                            errorDiv.style.cssText = 'padding: 2rem; text-align: center; color: var(--texto-secundario); background: rgba(255, 152, 0, 0.1); border-radius: 8px; border: 1px solid rgba(255, 152, 0, 0.3);';
                                            errorDiv.innerHTML = `
                                              <div style="margin-bottom: 0.5rem;">⚠️ Imagem não pôde ser carregada</div>
                                              <div style="font-size: 0.85rem; opacity: 0.8;">Use os botões abaixo para abrir ou baixar o documento</div>
                                            `;
                                            parent.appendChild(errorDiv);
                                          }
                                        }}
                                      />
                                    </div>
                                  )}
                                  {isPdf && (
                                    <div style={{ marginBottom: '0.75rem' }}>
                                      <iframe
                                        src={documentoUrl}
                                        style={{
                                          width: '100%',
                                          height: '600px',
                                          border: '1px solid var(--border-color)',
                                          borderRadius: '8px'
                                        }}
                                        title={`Documento PDF ${index + 1}`}
                                        onError={() => {
                                          console.error('Erro ao carregar PDF');
                                        }}
                                      />
                                    </div>
                                  )}
                                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                                    <button
                                      onClick={() => window.open(documentoUrl, '_blank')}
                                      style={{
                                        display: 'inline-block',
                                        padding: '0.5rem 1rem',
                                        background: 'var(--azul-dvs)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer',
                                        textDecoration: 'none'
                                      }}
                                    >
                                      🔗 Abrir em Nova Aba
                                    </button>
                                    <button
                                      onClick={handleDownload}
                                      type="button"
                                      style={{
                                        display: 'inline-block',
                                        padding: '0.5rem 1rem',
                                        background: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      📥 Baixar Documento
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Formulário de tratamento */}
                {!somenteVisualizacao && (
                <form onSubmit={handleUpdateStatus} className="tratamento-form">
                  <div className="form-group">
                    <label>Novo Status *</label>
                    <select
                      name="status"
                      value={tratamentoData.status}
                      onChange={handleTratamentoChange}
                      required
                    >
                      <option value="">Selecione o status</option>
                      <option value="pendente_bko">Pendente BKO</option>
                      <option value="tratando">Tratando</option>
                      <option value="pendente_vendedor">Pendente Vendedor</option>
                      <option value="pendente_sistemica">Pendente Sistêmica</option>
                      <option value="pendente_biometria">Pendente Biometria</option>
                      <option value="pendente_auditoria">Pendente Auditoria</option>
                      <option value="pendente_imput">Pendente Imput</option>
              <option value="tramitacao_manual">Tramitação Manual</option>
              <option value="critica">Crítica</option>
              <option value="mesa_de_credito">Mesa de Crédito</option>
                      <option value="aguardando_whatsapp">Aguardando WhatsApp</option>
                      <option value="aguardando_dados_sfa">Aguardando dados SFA</option>
                      <option value="validacao_ok">Validação OK</option>
                      <option value="aguardando_instalacao">Aguardando Instalação</option>
                      <option value="aguardando_aceite">Aguardando Aceite</option>
                      <option value="instalacao_cancelada">Instalação cancelada</option>
                      <option value="concluido">Concluído</option>
                      <option value="cancelado">Cancelado</option>
                      <option value="desistencia">Desistencia</option>
                      <option value="estorno">Estorno</option>
                      <option value="sem_viabilidade">Sem Viabilidade</option>
                      <option value="aguardando_estruturante">Aguardando Estruturante</option>
                      <option value="aguardando_can_ou_reag">Aguardando CAN ou REAG</option>
                      <option value="cancelamento_comercial">Cancelamento comercial</option>
                      <option value="cancelamento_tecnico">Cancelamento técnico</option>
                    </select>
                  </div>

                  {tratamentoData.status === 'validacao_ok' && validacaoOkModal === 'dados' && (
                    <>
                      <div className="form-group">
                        <label>Data da Instalação *</label>
                        <input
                          type="date"
                          name="data_instalacao"
                          value={tratamentoData.data_instalacao || ''}
                          onChange={handleTratamentoChange}
                          required
                        />
                        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                          Data em que está agendada a instalação.
                        </p>
                      </div>
                      <div className="form-group">
                        <label>Número da Ordem *</label>
                        <input
                          type="text"
                          name="numero_ordem"
                          value={tratamentoData.numero_ordem || ''}
                          onChange={handleTratamentoChange}
                          placeholder="Ex: 123456"
                          required
                          style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                        />
                        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                          Número da ordem de instalação (obrigatório).
                        </p>
                      </div>
                    </>
                  )}

                  {tratamentoData.status === 'aguardando_instalacao' && (
                    <>
                      <div className="form-group">
                        <label>Data da Instalação *</label>
                        <input
                          type="date"
                          name="data_instalacao"
                          value={tratamentoData.data_instalacao || ''}
                          onChange={handleTratamentoChange}
                          required
                        />
                        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                          Data em que está agendada a instalação (usada no filtro da página Vendas).
                        </p>
                      </div>
                      <div className="form-group">
                        <label>Número da Ordem</label>
                        <input
                          type="text"
                          name="numero_ordem"
                          value={tratamentoData.numero_ordem || ''}
                          onChange={handleTratamentoChange}
                          placeholder="Ex: 123456"
                          style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                        />
                        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                          Número da ordem de instalação (opcional).
                        </p>
                      </div>
                    </>
                  )}

                  {tratamentoData.status === 'concluido' && (
                    <div className="form-group">
                      <label>Data de Conclusão *</label>
                      <input
                        type="date"
                        name="data_conclusao"
                        value={tratamentoData.data_conclusao || ''}
                        onChange={handleTratamentoChange}
                        required
                      />
                      <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                        Data em que a venda foi concluída.
                      </p>
                    </div>
                  )}

                  {tratamentoData.status === 'aguardando_aceite' && (
                    <>
                      <div className="form-group">
                        <label>Simulação *</label>
                        <input
                          type="text"
                          name="simulacao"
                          value={tratamentoData.simulacao || ''}
                          onChange={handleTratamentoChange}
                          placeholder="Ex: S000005300754"
                          required
                          style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                        />
                      </div>
                      <div className="form-group">
                        <label>Link de Aceite *</label>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input
                            type="url"
                            name="link_aceite"
                            value={tratamentoData.link_aceite || ''}
                            onChange={handleTratamentoChange}
                            placeholder="https://assinaturadigital.vivo.com.br/sign/..."
                            required
                            style={{ flex: 1, padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const linkPrefix = 'https://assinaturadigital.vivo.com.br/sign/';
                              const linkCompleto = linkPrefix + (tratamentoData.link_aceite?.replace(linkPrefix, '') || '');
                              setTratamentoData(prev => ({ ...prev, link_aceite: linkCompleto }));
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              borderRadius: '6px',
                              border: 'none',
                              background: '#00a3e0',
                              color: 'white',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                            title="Inserir prefixo do link"
                          >
                            📎 Inserir Link
                          </button>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                          Clique em "Inserir Link" para adicionar o prefixo e complete com o código do aceite.
                        </p>
                      </div>
                    </>
                  )}

                  {tratamentoData.status === 'pendente_biometria' && (
                    <div className="form-group">
                      <label>Número do Pedido PAP *</label>
                      <input
                        type="text"
                        name="numero_pedido_pap"
                        value={tratamentoData.numero_pedido_pap || ''}
                        onChange={handleTratamentoChange}
                        placeholder="Ex: 123456"
                        required
                        style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                      />
                      <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                        Número do pedido PAP fornecido pela operadora.
                      </p>
                    </div>
                  )}

                  {(tratamentoData.status === 'sem_viabilidade' || tratamentoData.status === 'cancelado') && (
                    <>
                      <div className="form-group">
                        <label>Motivo *</label>
                        <textarea
                          name="motivo_imagem"
                          value={tratamentoData.motivo_imagem || ''}
                          onChange={handleTratamentoChange}
                          placeholder="Digite o motivo do cancelamento ou sem viabilidade..."
                          rows="3"
                          required
                          style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                        />
                      </div>
                      <div className="form-group">
                        <label>Imagem de Comprovação (Obrigatória) *</label>
                        <div
                          className="imagem-anexo-area"
                          onPaste={handlePasteImagem}
                          style={{
                            border: '2px dashed var(--border-color)',
                            borderRadius: '8px',
                            padding: '20px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            background: 'var(--bg-secondary)',
                            marginTop: '0.5rem'
                          }}
                        >
                          <input
                            type="file"
                            id="imagem-upload"
                            accept="image/*"
                            onChange={handleSelectImagem}
                            style={{ display: 'none' }}
                          />
                          <label htmlFor="imagem-upload" style={{ cursor: 'pointer' }}>
                            {imagemPreview ? (
                              <div>
                                <img
                                  src={imagemPreview}
                                  alt="Preview"
                                  className="imagem-anexo-preview"
                                  style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                                />
                                <p style={{ marginTop: '10px', fontSize: '0.85rem', color: 'var(--texto-secundario)' }}>
                                  Clique para trocar a imagem
                                </p>
                              </div>
                            ) : (
                              <div>
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px' }}>📷 Clique para upload ou Ctrl+V para colar</p>
                                <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)' }}>
                                  Formatos aceitos: JPG, PNG, GIF, WEBP
                                </p>
                              </div>
                            )}
                          </label>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem' }}>
                          Anexe um print ou imagem que comprove o cancelamento ou sem viabilidade.
                        </p>
                      </div>
                    </>
                  )}

                  <div className="form-group">
                    <label>Observação Adicional (Opcional)</label>
                    <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginBottom: '0.5rem' }}>
                      Esta observação será adicionada ao histórico automaticamente. O histórico completo acima não pode ser editado.
                    </p>
                    <textarea
                      name="observacao_tratamento"
                      value={tratamentoData.observacao_tratamento}
                      onChange={handleTratamentoChange}
                      placeholder="Digite uma observação adicional sobre o tratamento desta venda (opcional)..."
                      rows="4"
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={closeTratamento}
                      className="btn-secondary"
                      disabled={updating}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={updating}
                    >
                      {updating ? '⏳ Atualizando...' : '✅ Atualizar Status'}
                    </button>
                  </div>
                </form>
                )}
              </div>
            </div>

            {/* Modal de Detalhes da Venda (usado do histórico de vendas anteriores) */}
            {vendaSelecionadaParaModal && (
              <ModalPortal>
              <div className="modal-overlay modal-overlay--stacked" onClick={fecharModalDetalhes}>
                <div className="modal-content-venda" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header-venda">
                    <h2>👁️ Detalhes da Venda #{vendaSelecionadaParaModal.id}</h2>
                    <span className="close-modal" onClick={fecharModalDetalhes}>×</span>
                  </div>
                  
                  <div className="modal-body-venda">
                    {/* Informações do Cliente */}
                    <div className="info-section">
                      <h4>👤 Informações do Cliente</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Nome/Razão Social</div>
                          <div className="info-value">
                            {vendaSelecionadaParaModal.tipo_cliente === 'cnpj' 
                              ? (vendaSelecionadaParaModal.razao_social || vendaSelecionadaParaModal.nome_cliente)
                              : vendaSelecionadaParaModal.nome_cliente
                            }
                          </div>
                        </div>
                        {vendaSelecionadaParaModal.tipo_cliente === 'cnpj' && vendaSelecionadaParaModal.nome_cliente && (
                          <div className="info-item">
                            <div className="info-label">Nome do Cliente</div>
                            <div className="info-value">{vendaSelecionadaParaModal.nome_cliente}</div>
                          </div>
                        )}
                        <div className="info-item">
                          <div className="info-label">Tipo</div>
                          <div className="info-value">
                            {vendaSelecionadaParaModal.tipo_cliente === 'cpf' ? 'CPF' : 'CNPJ'}
                          </div>
                        </div>
                        {vendaSelecionadaParaModal.tipo_cliente === 'cpf' && (
                          <div className="info-item">
                            <div className="info-label">Marca</div>
                            <div className="info-value">{formatMarca(vendaSelecionadaParaModal.marca)}</div>
                          </div>
                        )}
                        <div className="info-item">
                          <div className="info-label">Documento</div>
                          <div className="info-value">{vendaSelecionadaParaModal.tipo_cliente === 'cnpj' ? (vendaSelecionadaParaModal.documento ? vendaSelecionadaParaModal.documento.replace(/\D/g, '') : '-') : formatDocumento(vendaSelecionadaParaModal.documento)}</div>
                        </div>
                        {vendaSelecionadaParaModal.tipo_cliente === 'cnpj' && vendaSelecionadaParaModal.cpf && (
                          <div className="info-item">
                            <div className="info-label">CPF do Representante</div>
                            <div className="info-value">{vendaSelecionadaParaModal.cpf.replace(/\D/g, '')}</div>
                          </div>
                        )}
                        {vendaSelecionadaParaModal.tipo_cliente === 'cpf' && vendaSelecionadaParaModal.cpf && (
                          <div className="info-item">
                            <div className="info-label">CPF</div>
                            <div className="info-value">{formatCPF(vendaSelecionadaParaModal.cpf)}</div>
                          </div>
                        )}
                        {vendaSelecionadaParaModal.nome_representante && (
                          <div className="info-item">
                            <div className="info-label">Nome do Representante</div>
                            <div className="info-value">{vendaSelecionadaParaModal.nome_representante}</div>
                          </div>
                        )}
                        {vendaSelecionadaParaModal.rg && (
                          <div className="info-item">
                            <div className="info-label">RG</div>
                            <div className="info-value">{vendaSelecionadaParaModal.rg}</div>
                          </div>
                        )}
                        {vendaSelecionadaParaModal.nome_mae && (
                          <div className="info-item">
                            <div className="info-label">Nome da Mãe</div>
                            <div className="info-value">{vendaSelecionadaParaModal.nome_mae}</div>
                          </div>
                        )}
                        {vendaSelecionadaParaModal.data_nascimento && (
                          <div className="info-item">
                            <div className="info-label">Data de Nascimento</div>
                            <div className="info-value">{formatDateOnly(vendaSelecionadaParaModal.data_nascimento)}</div>
                          </div>
                        )}
                        <div className="info-item">
                          <div className="info-label">Telefone</div>
                          <div className="info-value">{vendaSelecionadaParaModal.tipo_cliente === 'cnpj' ? (vendaSelecionadaParaModal.telefone ? vendaSelecionadaParaModal.telefone.replace(/\D/g, '') : '-') : formatPhone(vendaSelecionadaParaModal.telefone)}</div>
                        </div>
                        {vendaSelecionadaParaModal.telefone2 && (
                          <div className="info-item">
                            <div className="info-label">Telefone 2</div>
                            <div className="info-value">{vendaSelecionadaParaModal.tipo_cliente === 'cnpj' ? vendaSelecionadaParaModal.telefone2.replace(/\D/g, '') : formatPhone(vendaSelecionadaParaModal.telefone2)}</div>
                          </div>
                        )}
                        {vendaSelecionadaParaModal.email && (
                          <div className="info-item">
                            <div className="info-label">Email</div>
                            <div className="info-value">{vendaSelecionadaParaModal.email.toLowerCase()}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Endereço */}
                    <div className="info-section">
                      <h4>📍 Endereço</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">CEP</div>
                          <div className="info-value">{vendaSelecionadaParaModal.cep}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Logradouro</div>
                          <div className="info-value">{vendaSelecionadaParaModal.logradouro}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Número</div>
                          <div className="info-value">{vendaSelecionadaParaModal.numero}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Bairro</div>
                          <div className="info-value">{vendaSelecionadaParaModal.bairro}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Cidade</div>
                          <div className="info-value">{vendaSelecionadaParaModal.cidade}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Estado</div>
                          <div className="info-value">
                            {vendaSelecionadaParaModal.estado_completo} ({vendaSelecionadaParaModal.estado})
                          </div>
                        </div>
                        {vendaSelecionadaParaModal.complemento1 && (
                          <div className="info-item">
                            <div className="info-label">Complemento 1</div>
                            <div className="info-value">{vendaSelecionadaParaModal.complemento1}</div>
                          </div>
                        )}
                        {vendaSelecionadaParaModal.complemento2 && (
                          <div className="info-item">
                            <div className="info-label">Complemento 2</div>
                            <div className="info-value">{vendaSelecionadaParaModal.complemento2}</div>
                          </div>
                        )}
                        {vendaSelecionadaParaModal.complemento3 && (
                          <div className="info-item">
                            <div className="info-label">Complemento 3</div>
                            <div className="info-value">{vendaSelecionadaParaModal.complemento3}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Agendamento */}
                    <div className="info-section">
                      <h4>📅 Agendamento de Instalação</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Data Opção 1</div>
                          <div className="info-value">
                            {formatDateOnly(vendaSelecionadaParaModal.data_agendamento1)} - 
                            {vendaSelecionadaParaModal.periodo_agendamento1?.toUpperCase()}
                          </div>
                        </div>
                        {vendaSelecionadaParaModal.data_agendamento2 && (
                          <div className="info-item">
                            <div className="info-label">Data Opção 2</div>
                            <div className="info-value">
                              {formatDateOnly(vendaSelecionadaParaModal.data_agendamento2)} - 
                              {vendaSelecionadaParaModal.periodo_agendamento2?.toUpperCase()}
                            </div>
                          </div>
                        )}
                        <div className="info-item">
                          <div className="info-label">Aceita Antecipação</div>
                          <div className="info-value">
                            {vendaSelecionadaParaModal.aceita_antecipacao ? 'Sim' : 'Não'}
                          </div>
                        </div>
                        {vendaSelecionadaParaModal.observacao_instalacao && (
                          <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                            <div className="info-label">Observação sobre Instalação</div>
                            <div className="info-value">{vendaSelecionadaParaModal.observacao_instalacao}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Financeiro */}
                    <div className="info-section">
                      <h4>💰 Informações Financeiras</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Valor Total</div>
                          <div className="info-value" style={{ color: 'var(--azul-dvs)', fontWeight: '600', fontSize: '1.125rem' }}>
                            R$ {parseFloat(vendaSelecionadaParaModal.valor_total || 0).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Data de Vencimento</div>
                          <div className="info-value">Dia {vendaSelecionadaParaModal.data_vencimento}</div>
                        </div>
                      </div>
                    </div>

                    {/* Simulação e Link de Aceite */}
                    {(vendaSelecionadaParaModal.simulacao || vendaSelecionadaParaModal.link_aceite) && (
                      <div className="info-section">
                        <h4>📄 Simulação e Aceite</h4>
                        <div className="info-grid">
                          {vendaSelecionadaParaModal.simulacao && (
                            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                              <div className="info-label">Número da Simulação</div>
                              <div className="info-value" style={{ 
                                fontFamily: 'monospace', 
                                fontSize: '1rem',
                                background: 'var(--cinza-escuro-2)',
                                padding: '0.75rem 1rem',
                                borderRadius: '6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                <span>{vendaSelecionadaParaModal.simulacao}</span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(vendaSelecionadaParaModal.simulacao);
                                    alert('Número da simulação copiado!');
                                  }}
                                  className="btn-copy"
                                  title="Copiar número"
                                >
                                  📋
                                </button>
                              </div>
                            </div>
                          )}
                          {vendaSelecionadaParaModal.link_aceite && (
                            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                              <div className="info-label">Link de Aceite</div>
                              <div className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <a 
                                  href={vendaSelecionadaParaModal.link_aceite} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ 
                                    color: 'var(--azul-dvs)', 
                                    wordBreak: 'break-all',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 1rem',
                                    background: 'var(--cinza-escuro-2)',
                                    borderRadius: '6px',
                                    textDecoration: 'none'
                                  }}
                                >
                                  🔗 {vendaSelecionadaParaModal.link_aceite}
                                </a>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(vendaSelecionadaParaModal.link_aceite);
                                    alert('Link copiado para a área de transferência!');
                                  }}
                                  className="btn-copy"
                                  title="Copiar link"
                                >
                                  📋 Copiar
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Número do Pedido PAP */}
                    {vendaSelecionadaParaModal.numero_pedido_pap && (
                      <div className="info-section">
                        <h4>📋 Pedido PAP</h4>
                        <div className="info-grid">
                          <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                            <div className="info-label">Número do Pedido PAP</div>
                            <div className="info-value" style={{ 
                              fontFamily: 'monospace', 
                              fontSize: '1rem',
                              background: 'var(--cinza-escuro-2)',
                              padding: '0.75rem 1rem',
                              borderRadius: '6px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <span>{vendaSelecionadaParaModal.numero_pedido_pap}</span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(vendaSelecionadaParaModal.numero_pedido_pap);
                                  alert('Número do pedido PAP copiado!');
                                }}
                                className="btn-copy"
                                title="Copiar número do pedido PAP"
                              >
                                📋
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Dados PAP do Vendedor - mostrar para vendas CPF quando existir */}
                    {vendaSelecionadaParaModal.tipo_cliente === 'cpf' && vendaSelecionadaParaModal.vendedor_codigo_pap && (
                      <div className="info-section">
                        <h4>📋 Dados PAP do Vendedor</h4>
                        <div className="info-grid">
                          <div className="info-item">
                            <div className="info-label">Código PAP</div>
                            <div className="info-value" style={{ fontFamily: 'monospace', fontWeight: '600', color: 'var(--azul-dvs)' }}>
                              {vendaSelecionadaParaModal.vendedor_codigo_pap}
                            </div>
                          </div>
                          <div className="info-item">
                            <div className="info-label">Login PAP</div>
                            <div className="info-value">
                              {vendaSelecionadaParaModal.vendedor_login_pap || '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Observações */}
                    {vendaSelecionadaParaModal.observacao_venda && (
                      <div className="info-section">
                        <h4>📝 Observações da Venda</h4>
                        <div className="observacao-venda-value">
                          {vendaSelecionadaParaModal.observacao_venda}
                        </div>
                      </div>
                    )}

                    {/* Histórico de Observações de Tratamento e Troca de Status */}
                    {vendaSelecionadaParaModal.observacao_tratamento && (
                      <div className="info-section">
                        <h4>📋 Histórico de Tratamento e Mudanças de Status</h4>
                        <div className="historico-tratamento-container">
                          {parseObservacoesTratamento(vendaSelecionadaParaModal.observacao_tratamento).map((obs, index) => (
                            <div
                              key={index}
                              className={`observacao-item ${obs.tipo === 'mudanca_status' ? 'mudanca-status' : ''}`}
                            >
                              {obs.tipo === 'mudanca_status' ? (
                                <>
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.5rem',
                                    marginBottom: '0.75rem'
                                  }}>
                                    <span style={{ 
                                      fontSize: '1.2rem',
                                      filter: 'drop-shadow(0 0 4px rgba(6, 102, 255, 0.5))'
                                    }}>🔄</span>
                                    <span style={{ 
                                      fontWeight: '600', 
                                      color: 'var(--azul-dvs)',
                                      fontSize: '0.95rem'
                                    }}>
                                      Mudança de Status
                                    </span>
                                    {obs.dataHora && (
                                      <span style={{ 
                                        marginLeft: 'auto',
                                        fontSize: '0.85rem',
                                        color: 'var(--texto-secundario)',
                                        opacity: 0.8
                                      }}>
                                        {obs.dataHora}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.75rem',
                                    flexWrap: 'wrap'
                                  }}>
                                    <div style={{
                                      padding: '0.4rem 0.8rem',
                                      background: 'rgba(255, 152, 0, 0.2)',
                                      border: '1px solid rgba(255, 152, 0, 0.4)',
                                      borderRadius: '6px',
                                      fontSize: '0.85rem',
                                      color: '#ff9800'
                                    }}>
                                      {obs.statusAnterior}
                                    </div>
                                    <span style={{ color: 'var(--texto-secundario)' }}>→</span>
                                    <div style={{
                                      padding: '0.4rem 0.8rem',
                                      background: 'rgba(6, 102, 255, 0.2)',
                                      border: '1px solid rgba(6, 102, 255, 0.4)',
                                      borderRadius: '6px',
                                      fontSize: '0.85rem',
                                      color: 'var(--azul-dvs)',
                                      fontWeight: '600'
                                    }}>
                                      {obs.statusNovo}
                                    </div>
                                  </div>
                                  {obs.usuario && (
                                    <div style={{ 
                                      marginTop: '0.75rem',
                                      fontSize: '0.85rem',
                                      color: 'var(--texto-secundario)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem'
                                    }}>
                                      <span>👤</span>
                                      <span>Por: <strong>{obs.usuario}</strong></span>
                                    </div>
                                  )}
                                </>
                              ) : obs.tipo === 'observacao' ? (
                                <>
                                  <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '0.5rem',
                                    marginBottom: '0.75rem'
                                  }}>
                                    <span style={{ fontSize: '1.1rem' }}>💬</span>
                                    <span style={{ 
                                      fontWeight: '600', 
                                      color: 'var(--texto-primario)',
                                      fontSize: '0.95rem'
                                    }}>
                                      Observação
                                    </span>
                                    {obs.dataHora && (
                                      <span style={{ 
                                        marginLeft: 'auto',
                                        fontSize: '0.85rem',
                                        color: 'var(--texto-secundario)',
                                        opacity: 0.8
                                      }}>
                                        {obs.dataHora}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ 
                                    color: 'var(--texto-primario)',
                                    lineHeight: '1.6',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}>
                                    {obs.texto}
                                  </div>
                                  {obs.usuario && (
                                    <div style={{ 
                                      marginTop: '0.75rem',
                                      fontSize: '0.85rem',
                                      color: 'var(--texto-secundario)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem'
                                    }}>
                                      <span>👤</span>
                                      <span>Por: <strong>{obs.usuario}</strong></span>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div style={{ 
                                  color: 'var(--texto-primario)',
                                  lineHeight: '1.6',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}>
                                  {obs.texto}
                                </div>
                              )}
                            </div>
                          ))}
                          {parseObservacoesTratamento(vendaSelecionadaParaModal.observacao_tratamento).length === 0 && (
                            <div style={{ 
                              textAlign: 'center', 
                              padding: '2rem',
                              color: 'var(--texto-secundario)',
                              fontStyle: 'italic'
                            }}>
                              Nenhuma observação de tratamento registrada
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Status */}
                    <div className="info-section">
                      <h4>ℹ️ Status e Informações</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Status</div>
                          <div className="info-value">
                            {vendaSelecionadaParaModal.status === 'aguardando_instalacao' && vendaSelecionadaParaModal.data_instalacao && (
                              <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                                Data instalação: {formatDateOnly(vendaSelecionadaParaModal.data_instalacao)}
                              </div>
                            )}
                            {vendaSelecionadaParaModal.status === 'aguardando_instalacao' && vendaSelecionadaParaModal.numero_ordem && (
                              <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                                Ordem: {vendaSelecionadaParaModal.numero_ordem}
                              </div>
                            )}
                            {vendaSelecionadaParaModal.status === 'concluido' && vendaSelecionadaParaModal.data_conclusao && (
                              <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                                Concluído em: {formatDateOnly(vendaSelecionadaParaModal.data_conclusao)}
                              </div>
                            )}
                            <span className={`status-badge ${getStatusClass(vendaSelecionadaParaModal.status)}`}>
                              {getStatusText(vendaSelecionadaParaModal.status)}
                            </span>
                          </div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Vendedor</div>
                          <div className="info-value">{vendaSelecionadaParaModal.vendedor?.nome || 'N/A'}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Cadastrado em</div>
                          <div className="info-value">{formatDateTime(vendaSelecionadaParaModal.created_at)}</div>
                        </div>
                        {vendaSelecionadaParaModal.tratado_por && (
                          <>
                            <div className="info-item">
                              <div className="info-label">Tratado por</div>
                              <div className="info-value">{vendaSelecionadaParaModal.tratador?.nome}</div>
                            </div>
                            <div className="info-item">
                              <div className="info-label">Tratado em</div>
                              <div className="info-value">{formatDateTime(vendaSelecionadaParaModal.tratado_em)}</div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Informações de Viabilidade */}
                    {vendaSelecionadaParaModal.operadora_selecionada && (
                      <div className="info-section">
                        <h4>📡 Informações de Viabilidade</h4>
                        <div className="info-grid">
                          <div className="info-item">
                            <div className="info-label">Operadora Selecionada</div>
                            <div className="info-value" style={{ fontWeight: '600' }}>
                              {vendaSelecionadaParaModal.operadora_selecionada.charAt(0).toUpperCase() + vendaSelecionadaParaModal.operadora_selecionada.slice(1)}
                            </div>
                          </div>
                          {vendaSelecionadaParaModal.operadora_viabilidade_data && (
                            <div className="info-item">
                              <div className="info-label">Data da Consulta</div>
                              <div className="info-value">
                                {formatDateTime(vendaSelecionadaParaModal.operadora_viabilidade_data)}
                              </div>
                            </div>
                          )}
                          {vendaSelecionadaParaModal.operadora_viabilidade_json && (
                            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                              <div className="info-label">Status de Viabilidade</div>
                              <div className="info-value">
                                {(() => {
                                  try {
                                    let viabilidade = vendaSelecionadaParaModal.operadora_viabilidade_json;
                                    
                                    // Se for string, fazer parse
                                    if (typeof viabilidade === 'string') {
                                      try {
                                        viabilidade = JSON.parse(viabilidade);
                                        // Se o resultado ainda for string (double-encoded), fazer parse novamente
                                        if (typeof viabilidade === 'string') {
                                          viabilidade = JSON.parse(viabilidade);
                                        }
                                      } catch (parseError) {
                                        console.error('Erro ao fazer parse do JSON:', parseError);
                                        return <span style={{ color: '#dc3545' }}>Erro ao processar JSON de viabilidade</span>;
                                      }
                                    }
                                    
                                    console.log('📋 Viabilidade JSON parseado (Modal Detalhes):', viabilidade);
                                    
                                    // Se viabilidade é null ou undefined
                                    if (!viabilidade) {
                                      return <span style={{ color: '#868e96' }}>Dados de viabilidade não disponíveis</span>;
                                    }
                                    
                                    // Verificar se tem operadoras diretamente ou dentro de uma propriedade
                                    let operadoras = null;
                                    if (viabilidade.operadoras && typeof viabilidade.operadoras === 'object') {
                                      operadoras = viabilidade.operadoras;
                                    } else if (viabilidade.data && viabilidade.data.operadoras) {
                                      operadoras = viabilidade.data.operadoras;
                                    } else if (typeof viabilidade === 'object' && !Array.isArray(viabilidade)) {
                                      const keys = Object.keys(viabilidade);
                                      const hasOperadoraStructure = keys.some(key => {
                                        const val = viabilidade[key];
                                        return val && typeof val === 'object' && ('coverage' in val);
                                      });
                                      if (hasOperadoraStructure) {
                                        operadoras = viabilidade;
                                      }
                                    }
                                    
                                    if (!operadoras || (typeof operadoras === 'object' && Object.keys(operadoras).length === 0)) {
                                      console.warn('Viabilidade sem operadoras válidas:', viabilidade);
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                          <span style={{ color: '#868e96' }}>Dados de viabilidade não contêm operadoras</span>
                                          <details style={{ fontSize: '0.85rem', color: '#868e96' }}>
                                            <summary style={{ cursor: 'pointer' }}>Ver estrutura do JSON</summary>
                                            <pre style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#1e1e1e', borderRadius: '4px', overflow: 'auto', maxHeight: '200px' }}>
                                              {JSON.stringify(viabilidade, null, 2)}
                                            </pre>
                                          </details>
                                        </div>
                                      );
                                    }

                                    // Normalizar nome da operadora (case-insensitive, remover espaços extras)
                                    const operadoraSelecionadaNormalizada = vendaSelecionadaParaModal.operadora_selecionada 
                                      ? vendaSelecionadaParaModal.operadora_selecionada.toLowerCase().trim()
                                      : null;

                                    // Procurar operadora no JSON (case-insensitive)
                                    let operadora = null;
                                    if (operadoraSelecionadaNormalizada && operadoras) {
                                      // Tentar encontrar pelo nome exato primeiro
                                      operadora = operadoras[vendaSelecionadaParaModal.operadora_selecionada];
                                      
                                      // Se não encontrar, procurar case-insensitive
                                      if (!operadora) {
                                        const operadorasKeys = Object.keys(operadoras);
                                        const operadoraEncontrada = operadorasKeys.find(key => 
                                          key.toLowerCase().trim() === operadoraSelecionadaNormalizada
                                        );
                                        if (operadoraEncontrada) {
                                          operadora = operadoras[operadoraEncontrada];
                                        }
                                      }
                                    }

                                    if (!operadora) {
                                      console.warn('Operadora não encontrada na viabilidade:', {
                                        operadoraSelecionada: vendaSelecionadaParaModal.operadora_selecionada,
                                        operadoras: Object.keys(operadoras)
                                      });
                                      return <span style={{ color: '#868e96' }}>Operadora não encontrada nos dados de viabilidade</span>;
                                    }
                                    
                                    const hasCoverage = operadora.coverage === true;
                                    console.log('✅ Viabilidade da operadora:', { operadora, hasCoverage });
                                    
                                    return (
                                      <span className={`status-badge ${hasCoverage ? 'status-ativo' : 'status-inativo'}`} style={{ 
                                        background: hasCoverage ? '#4caf50' : '#ff9800',
                                        color: '#000 !important',
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '4px',
                                        fontWeight: '600'
                                      }}>
                                        {hasCoverage ? '✓ Viabilidade Confirmada' : '⚠ Sem Viabilidade Confirmada'}
                                      </span>
                                    );
                                  } catch (e) {
                                    console.error('Erro ao processar viabilidade:', e);
                                    return <span style={{ color: '#dc3545' }}>Erro ao processar dados de viabilidade</span>;
                                  }
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Envio de Aceite */}
                    <div className="info-section">
                      <h4>📧 Envio de Aceite</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">Operadora</div>
                          <div className="info-value">
                            {vendaSelecionadaParaModal.envio_aceite ? (() => {
                              const parts = vendaSelecionadaParaModal.envio_aceite.split(':');
                              return parts.length > 0 
                                ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
                                : '-';
                            })() : 'Não informado'}
                          </div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Meios de Envio</div>
                          <div className="info-value">
                            {vendaSelecionadaParaModal.envio_aceite ? (() => {
                              const parts = vendaSelecionadaParaModal.envio_aceite.split(':');
                              if (parts.length < 2) return '-';
                              
                              const meios = parts[1].split(',');
                              return <MeiosEnvioList meios={meios} />;
                            })() : 'Não informado'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Documentos Anexos */}
                    {vendaSelecionadaParaModal.documento_anexo && (
                      <div className="info-section">
                        <h4>📎 Documentos Anexos</h4>
                        <div className="info-value" style={{ marginTop: '1rem' }}>
                          {(() => {
                            // Tentar fazer parse do JSON (múltiplos arquivos) ou usar como string (arquivo único - compatibilidade)
                            let documentos = [];
                            try {
                              const parsed = JSON.parse(vendaSelecionadaParaModal.documento_anexo);
                              documentos = Array.isArray(parsed) ? parsed : [parsed];
                            } catch (e) {
                              // Se não for JSON, tratar como string única (compatibilidade com dados antigos)
                              documentos = [vendaSelecionadaParaModal.documento_anexo];
                            }
                            
                            // Função para construir URL correta do documento
                            const getDocumentoUrl = (documentoPath) => {
                              // Se o caminho já começa com http, retornar como está
                              if (documentoPath.startsWith('http://') || documentoPath.startsWith('https://')) {
                                return documentoPath;
                              }
                              
                              // Usar a mesma URL base da API configurada no axios
                              let baseUrl = getApiBaseUrl();
                              
                              // Remover /api do final se existir (os uploads são servidos na raiz do servidor)
                              baseUrl = baseUrl.replace(/\/api\/?$/, '');
                              
                              // Remover barra final se existir
                              baseUrl = baseUrl.replace(/\/$/, '');
                              
                              // Garantir que o caminho tenha o formato correto
                              // Se não começar com "uploads/", adicionar
                              let path = documentoPath;
                              if (!path.startsWith('uploads/') && !path.startsWith('/uploads/')) {
                                // Se tiver apenas o nome do arquivo, adicionar o caminho completo
                                if (!path.includes('/')) {
                                  path = `uploads/documentos/${path}`;
                                }
                              }
                              
                              // Garantir que o caminho comece com /
                              path = path.startsWith('/') ? path : `/${path}`;
                              
                              const fullUrl = `${baseUrl}${path}`;
                              console.log('📂 Construindo URL do documento:', {
                                documentoPath,
                                baseUrl,
                                path,
                                fullUrl
                              });
                              return fullUrl;
                            };
                            
                            return (
                              <>
                                {documentos.map((documentoPath, index) => {
                                  const documentoUrl = getDocumentoUrl(documentoPath);
                                  const isImage = /\.(jpg|jpeg|png)$/i.test(documentoPath);
                                  const isPdf = /\.pdf$/i.test(documentoPath);
                                  const fileName = documentoPath.split('/').pop();
                                  
                                  // Função para fazer download
                                  const handleDownload = async (e) => {
                                    e.preventDefault();
                                    console.log('📥 Tentando baixar:', documentoUrl);
                                    
                                    try {
                                      // Tentar usar fetch primeiro
                                      const response = await fetch(documentoUrl, {
                                        method: 'GET',
                                        headers: {
                                          'Accept': '*/*'
                                        }
                                      });
                                      
                                      if (!response.ok) {
                                        console.error('❌ Erro na resposta:', response.status, response.statusText);
                                        throw new Error(`Arquivo não encontrado (${response.status})`);
                                      }
                                      
                                      const blob = await response.blob();
                                      const url = window.URL.createObjectURL(blob);
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = fileName;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                      window.URL.revokeObjectURL(url);
                                      console.log('✅ Download iniciado com sucesso');
                                    } catch (error) {
                                      console.error('❌ Erro ao baixar arquivo:', error);
                                      // Se fetch falhar, tentar abrir em nova aba como fallback
                                      alert(`⚠️ Não foi possível fazer o download automático. Abrindo em nova aba...\n\nErro: ${error.message}`);
                                      window.open(documentoUrl, '_blank');
                                    }
                                  };
                                  
                                  return (
                                    <div key={index} style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(42, 42, 42, 0.3)', borderRadius: '8px' }}>
                                      <div style={{ marginBottom: '0.5rem', fontWeight: '600', color: 'var(--texto-primario)' }}>
                                        📄 Documento {documentos.length > 1 ? `${index + 1}` : ''}: {fileName}
                                      </div>
                                      {isImage && (
                                        <div style={{ marginBottom: '0.75rem', position: 'relative' }}>
                                          <img 
                                            src={documentoUrl}
                                            alt={`Documento anexo ${index + 1}`}
                                            style={{ 
                                              maxWidth: '100%', 
                                              maxHeight: '500px', 
                                              border: '1px solid var(--border-color)',
                                              borderRadius: '8px',
                                              cursor: 'pointer',
                                              objectFit: 'contain',
                                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                              display: 'block'
                                            }}
                                            onClick={() => window.open(documentoUrl, '_blank')}
                                            onError={(e) => {
                                              // Se a imagem falhar ao carregar, mostrar mensagem
                                              const img = e.target;
                                              img.style.display = 'none';
                                              const parent = img.parentNode;
                                              // Verificar se já existe mensagem de erro
                                              if (!parent.querySelector('.image-error-message')) {
                                                const errorDiv = document.createElement('div');
                                                errorDiv.className = 'image-error-message';
                                                errorDiv.style.cssText = 'padding: 2rem; text-align: center; color: var(--texto-secundario); background: rgba(255, 152, 0, 0.1); border-radius: 8px; border: 1px solid rgba(255, 152, 0, 0.3);';
                                                errorDiv.innerHTML = `
                                                  <div style="margin-bottom: 0.5rem;">⚠️ Imagem não pôde ser carregada</div>
                                                  <div style="font-size: 0.85rem; opacity: 0.8;">Use os botões abaixo para abrir ou baixar o documento</div>
                                                `;
                                                parent.appendChild(errorDiv);
                                              }
                                            }}
                                          />
                                        </div>
                                      )}
                                      {isPdf && (
                                        <div style={{ marginBottom: '0.75rem' }}>
                                          <iframe
                                            src={documentoUrl}
                                            style={{
                                              width: '100%',
                                              height: '600px',
                                              border: '1px solid var(--border-color)',
                                              borderRadius: '8px'
                                            }}
                                            title={`Documento PDF ${index + 1}`}
                                            onError={() => {
                                              console.error('Erro ao carregar PDF');
                                            }}
                                          />
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                                        <button
                                          onClick={() => window.open(documentoUrl, '_blank')}
                                          style={{
                                            display: 'inline-block',
                                            padding: '0.5rem 1rem',
                                            background: 'var(--azul-dvs)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '0.9rem',
                                            cursor: 'pointer',
                                            textDecoration: 'none'
                                          }}
                                        >
                                          🔗 Abrir em Nova Aba
                                        </button>
                                        <button
                                          onClick={handleDownload}
                                          type="button"
                                          style={{
                                            display: 'inline-block',
                                            padding: '0.5rem 1rem',
                                            background: '#28a745',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '0.9rem',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          📥 Baixar Documento
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              </ModalPortal>
            )}

            {/* Modal de Histórico de Vendas Anteriores */}
            {vendaParaMostrarHistorico && (
              <ModalPortal>
              <div className="modal-overlay modal-overlay--stacked" onClick={fecharHistoricoVenda}>
                <div className="modal-content historico-vendas-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>📋 Histórico de Vendas Anteriores</h2>
                    <button onClick={fecharHistoricoVenda} className="btn-close">✕</button>
                  </div>
                  
                  <div className="modal-body">
                    {buscandoVendasAnteriores ? (
                      <div className="busca-contatos-loading">
                        Buscando vendas anteriores com os mesmos contatos...
                      </div>
                    ) : (
                      <>
                        <p style={{ color: 'var(--texto-primario)', marginBottom: '1rem' }}>
                          Vendas com o mesmo <strong>telefone</strong>, <strong>CPF</strong> ou <strong>email</strong> encontradas:
                        </p>
                        
                        {vendasAnteriores && Object.keys(vendasAnteriores).length > 0 ? (
                          <div className="historico-vendas-lista">
                            {Object.entries(vendasAnteriores).map(([tipo, vendas]) => {
                              if (!vendas || vendas.length === 0) return null;
                              
                              return (
                                <div key={tipo} style={{ marginBottom: '1.5rem' }}>
                                  <h4 style={{ 
                                    margin: '0 0 0.5rem 0', 
                                    fontSize: '1rem', 
                                    color: '#dc3545',
                                    textTransform: 'uppercase',
                                    borderBottom: '2px solid #dc3545',
                                    paddingBottom: '0.5rem'
                                  }}>
                                    {tipo === 'telefone' ? '📱 Telefones Repetidos' : 
                                     tipo === 'cpf' ? '🆔 CPFs Repetidos' : '📧 Emails Repetidos'}
                                    <span style={{ 
                                      marginLeft: '0.5rem', 
                                      background: '#dc3545', 
                                      color: 'white', 
                                      padding: '0.25rem 0.5rem', 
                                      borderRadius: '10px',
                                      fontSize: '0.8rem'
                                    }}>
                                      {vendas.length}
                                    </span>
                                  </h4>
                                  
                                  {vendas.map((vendaAnterior) => (
                                    <div 
                                      key={vendaAnterior.id} 
                                      className="historico-venda-item"
                                      onClick={() => {
                                        abrirModalDetalhesVenda(vendaAnterior);
                                      }}
                                    >
                                      <div className="historico-venda-item-header">
                                        <span className="historico-venda-id">#${vendaAnterior.id}</span>
                                        <span className={`historico-venda-status ${getStatusClass(vendaAnterior.status)}`}>
                                          {getStatusText(vendaAnterior.status)}
                                        </span>
                                      </div>
                                      <div className="historico-venda-info">
                                        <p style={{ margin: 0 }}>
                                          <strong>Cliente:</strong> {vendaAnterior.nome_cliente}
                                        </p>
                                        <p style={{ margin: 0, color: 'var(--texto-secundario)' }}>
                                          {vendaAnterior.telefone && <span>📞 {formatPhone(vendaAnterior.telefone)} • </span>}
                                          {vendaAnterior.cpf && <span>🆔 {formatCPF(vendaAnterior.cpf)} • </span>}
                                          {vendaAnterior.email && <span>📧 {vendaAnterior.email.toLowerCase()}</span>}
                                        </p>
                                        <p style={{ margin: 0, color: 'var(--texto-secundario)' }}>
                                          <strong>Data:</strong> {vendaAnterior.created_at ? formatDateOnly(vendaAnterior.created_at) : '-'}
                                        </p>
                                        {vendaAnterior.tratador && (
                                          <p style={{ margin: 0, color: 'var(--texto-secundario)' }}>
                                            <strong>Tratador:</strong> {vendaAnterior.tratador.nome}
                                          </p>
                                        )}
                                      </div>
                                      <div className="historico-venda-vendedor">
                                        <strong>Vendedor:</strong> {vendaAnterior.vendedor?.nome || '-'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--texto-secundario)' }}>
                            <p>Nenhuma venda anterior encontrada com os mesmos contatos.</p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Modal de Detalhes da Venda (usado do histórico de vendas anteriores) */}
                    {vendaSelecionadaParaModal && (
                      <ModalPortal>
                      <div className="modal-overlay modal-overlay--stacked" onClick={fecharModalDetalhes}>
                        <div className="modal-content-venda" onClick={(e) => e.stopPropagation()}>
                          <div className="modal-header-venda">
                            <h2>👁️ Detalhes da Venda #{vendaSelecionadaParaModal.id}</h2>
                            <span className="close-modal" onClick={fecharModalDetalhes}>×</span>
                          </div>
                          
                          <div className="modal-body-venda">
                            {/* Informações do Cliente */}
                            <div className="info-section">
                              <h4>👤 Informações do Cliente</h4>
                              <div className="info-grid">
                                <div className="info-item">
                                  <div className="info-label">Nome/Razão Social</div>
                                  <div className="info-value">
                                    {vendaSelecionadaParaModal.tipo_cliente === 'cnpj' 
                                      ? (vendaSelecionadaParaModal.razao_social || vendaSelecionadaParaModal.nome_cliente)
                                      : vendaSelecionadaParaModal.nome_cliente
                                    }
                                  </div>
                                </div>
                                {vendaSelecionadaParaModal.tipo_cliente === 'cnpj' && vendaSelecionadaParaModal.nome_cliente && (
                                  <div className="info-item">
                                    <div className="info-label">Nome do Cliente</div>
                                    <div className="info-value">{vendaSelecionadaParaModal.nome_cliente}</div>
                                  </div>
                                )}
                                <div className="info-item">
                                  <div className="info-label">Tipo</div>
                                  <div className="info-value">
                                    {vendaSelecionadaParaModal.tipo_cliente === 'cpf' ? 'CPF' : 'CNPJ'}
                                  </div>
                                </div>
                                {vendaSelecionadaParaModal.tipo_cliente === 'cpf' && (
                                  <div className="info-item">
                                    <div className="info-label">Marca</div>
                                    <div className="info-value">{formatMarca(vendaSelecionadaParaModal.marca)}</div>
                                  </div>
                                )}
                                <div className="info-item">
                                  <div className="info-label">Documento</div>
                                  <div className="info-value">{vendaSelecionadaParaModal.tipo_cliente === 'cnpj' ? (vendaSelecionadaParaModal.documento ? vendaSelecionadaParaModal.documento.replace(/\D/g, '') : '-') : formatDocumento(vendaSelecionadaParaModal.documento)}</div>
                                </div>
                                {vendaSelecionadaParaModal.tipo_cliente === 'cnpj' && vendaSelecionadaParaModal.cpf && (
                                  <div className="info-item">
                                    <div className="info-label">CPF do Representante</div>
                                    <div className="info-value">{vendaSelecionadaParaModal.cpf.replace(/\D/g, '')}</div>
                                  </div>
                                )}
                                {vendaSelecionadaParaModal.tipo_cliente === 'cpf' && vendaSelecionadaParaModal.cpf && (
                                  <div className="info-item">
                                    <div className="info-label">CPF</div>
                                    <div className="info-value">{formatCPF(vendaSelecionadaParaModal.cpf)}</div>
                                  </div>
                                )}
                                {vendaSelecionadaParaModal.rg && (
                                  <div className="info-item">
                                    <div className="info-label">RG</div>
                                    <div className="info-value">{vendaSelecionadaParaModal.rg}</div>
                                  </div>
                                )}
                                {vendaSelecionadaParaModal.nome_mae && (
                                  <div className="info-item">
                                    <div className="info-label">Nome da Mãe</div>
                                    <div className="info-value">{vendaSelecionadaParaModal.nome_mae}</div>
                                  </div>
                                )}
                                {vendaSelecionadaParaModal.data_nascimento && (
                                  <div className="info-item">
                                    <div className="info-label">Data de Nascimento</div>
                                    <div className="info-value">{formatDateOnly(vendaSelecionadaParaModal.data_nascimento)}</div>
                                  </div>
                                )}
                                <div className="info-item">
                                  <div className="info-label">Telefone</div>
                                  <div className="info-value">{vendaSelecionadaParaModal.tipo_cliente === 'cnpj' ? (vendaSelecionadaParaModal.telefone ? vendaSelecionadaParaModal.telefone.replace(/\D/g, '') : '-') : formatPhone(vendaSelecionadaParaModal.telefone)}</div>
                                </div>
                                {vendaSelecionadaParaModal.telefone2 && (
                                  <div className="info-item">
                                    <div className="info-label">Telefone 2</div>
                                    <div className="info-value">{vendaSelecionadaParaModal.tipo_cliente === 'cnpj' ? vendaSelecionadaParaModal.telefone2.replace(/\D/g, '') : formatPhone(vendaSelecionadaParaModal.telefone2)}</div>
                                  </div>
                                )}
                                {vendaSelecionadaParaModal.email && (
                                  <div className="info-item">
                                    <div className="info-label">Email</div>
                                    <div className="info-value">{vendaSelecionadaParaModal.email.toLowerCase()}</div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Endereço */}
                            <div className="info-section">
                              <h4>📍 Endereço</h4>
                              <div className="info-grid">
                                <div className="info-item">
                                  <div className="info-label">CEP</div>
                                  <div className="info-value">{vendaSelecionadaParaModal.cep}</div>
                                </div>
                                <div className="info-item">
                                  <div className="info-label">Logradouro</div>
                                  <div className="info-value">{vendaSelecionadaParaModal.logradouro}</div>
                                </div>
                                <div className="info-item">
                                  <div className="info-label">Número</div>
                                  <div className="info-value">{vendaSelecionadaParaModal.numero}</div>
                                </div>
                                <div className="info-item">
                                  <div className="info-label">Bairro</div>
                                  <div className="info-value">{vendaSelecionadaParaModal.bairro}</div>
                                </div>
                                <div className="info-item">
                                  <div className="info-label">Cidade</div>
                                  <div className="info-value">{vendaSelecionadaParaModal.cidade}</div>
                                </div>
                                <div className="info-item">
                                  <div className="info-label">Estado</div>
                                  <div className="info-value">
                                    {vendaSelecionadaParaModal.estado_completo} ({vendaSelecionadaParaModal.estado})
                                  </div>
                                </div>
                                {vendaSelecionadaParaModal.complemento1 && (
                                  <div className="info-item">
                                    <div className="info-label">Complemento 1</div>
                                    <div className="info-value">{vendaSelecionadaParaModal.complemento1}</div>
                                  </div>
                                )}
                                {vendaSelecionadaParaModal.complemento2 && (
                                  <div className="info-item">
                                    <div className="info-label">Complemento 2</div>
                                    <div className="info-value">{vendaSelecionadaParaModal.complemento2}</div>
                                  </div>
                                )}
                                {vendaSelecionadaParaModal.complemento3 && (
                                  <div className="info-item">
                                    <div className="info-label">Complemento 3</div>
                                    <div className="info-value">{vendaSelecionadaParaModal.complemento3}</div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Agendamento */}
                            <div className="info-section">
                              <h4>📅 Agendamento de Instalação</h4>
                              <div className="info-grid">
                                <div className="info-item">
                                  <div className="info-label">Data Opção 1</div>
                                  <div className="info-value">
                                    {formatDateOnly(vendaSelecionadaParaModal.data_agendamento1)} - 
                                    {vendaSelecionadaParaModal.periodo_agendamento1?.toUpperCase()}
                                  </div>
                                </div>
                                {vendaSelecionadaParaModal.data_agendamento2 && (
                                  <div className="info-item">
                                    <div className="info-label">Data Opção 2</div>
                                    <div className="info-value">
                                      {formatDateOnly(vendaSelecionadaParaModal.data_agendamento2)} - 
                                      {vendaSelecionadaParaModal.periodo_agendamento2?.toUpperCase()}
                                    </div>
                                  </div>
                                )}
                                <div className="info-item">
                                  <div className="info-label">Aceita Antecipação</div>
                                  <div className="info-value">
                                    {vendaSelecionadaParaModal.aceita_antecipacao ? 'Sim' : 'Não'}
                                  </div>
                                </div>
                                {vendaSelecionadaParaModal.observacao_instalacao && (
                                  <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                                    <div className="info-label">Observação sobre Instalação</div>
                                    <div className="info-value">{vendaSelecionadaParaModal.observacao_instalacao}</div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Financeiro */}
                            <div className="info-section">
                              <h4>💰 Informações Financeiras</h4>
                              <div className="info-grid">
                                <div className="info-item">
                                  <div className="info-label">Valor Total</div>
                                  <div className="info-value" style={{ color: 'var(--azul-dvs)', fontWeight: '600', fontSize: '1.125rem' }}>
                                    R$ {parseFloat(vendaSelecionadaParaModal.valor_total || 0).toLocaleString('pt-BR', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    })}
                                  </div>
                                </div>
                                <div className="info-item">
                                  <div className="info-label">Data de Vencimento</div>
                                  <div className="info-value">Dia {vendaSelecionadaParaModal.data_vencimento}</div>
                                </div>
                      </div>
                    </div>

                    {/* Simulação e Link de Aceite - Para vendas em Aguardando Aceite */}
                    {(vendaSelecionadaParaModal.simulacao || vendaSelecionadaParaModal.link_aceite) && (
                      <div className="info-section">
                        <h4>📋 Simulação e Link de Aceite</h4>
                        <div className="info-grid">
                          {vendaSelecionadaParaModal.simulacao && (
                            <div className="info-item">
                              <div className="info-label">Número da Simulação</div>
                              <div className="info-value" style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: '600', color: 'var(--azul-dvs)' }}>
                                {vendaSelecionadaParaModal.simulacao}
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(vendaSelecionadaParaModal.simulacao);
                                    alert('Número da simulação copiado!');
                                  }}
                                  className="btn-copy"
                                  style={{ marginLeft: '0.5rem' }}
                                  title="Copiar número da simulação"
                                >
                                  📋 Copiar
                                </button>
                              </div>
                            </div>
                          )}
                          {vendaSelecionadaParaModal.link_aceite && (
                            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                              <div className="info-label">Link de Aceite</div>
                              <div className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <a 
                                  href={vendaSelecionadaParaModal.link_aceite} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ 
                                    color: 'var(--azul-dvs)', 
                                    wordBreak: 'break-all',
                                    textDecoration: 'underline'
                                  }}
                                >
                                  {vendaSelecionadaParaModal.link_aceite}
                                </a>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(vendaSelecionadaParaModal.link_aceite);
                                    alert('Link copiado!');
                                  }}
                                  className="btn-copy"
                                  title="Copiar link"
                                >
                                  📋 Copiar
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Número do Pedido PAP */}
                    {vendaSelecionadaParaModal.numero_pedido_pap && (
                      <div className="info-section">
                        <h4>📋 Pedido PAP</h4>
                        <div className="info-grid">
                          <div className="info-item">
                            <div className="info-label">Número do Pedido PAP</div>
                            <div className="info-value" style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: '600', color: 'var(--azul-dvs)' }}>
                              {vendaSelecionadaParaModal.numero_pedido_pap}
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(vendaSelecionadaParaModal.numero_pedido_pap);
                                  alert('Número do pedido PAP copiado!');
                                }}
                                style={{
                                  marginLeft: '0.5rem',
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.75rem',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: 'var(--azul-dvs)',
                                  color: 'white',
                                  cursor: 'pointer'
                                }}
                                title="Copiar número do pedido PAP"
                              >
                                📋 Copiar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Dados PAP do Vendedor - mostrar para vendas CPF quando existir */}
                    {vendaSelecionadaParaModal.tipo_cliente === 'cpf' && vendaSelecionadaParaModal.vendedor_codigo_pap && (
                      <div className="info-section">
                        <h4>📋 Dados PAP do Vendedor</h4>
                        <div className="info-grid">
                          <div className="info-item">
                            <div className="info-label">Código PAP</div>
                            <div className="info-value" style={{ fontFamily: 'monospace', fontWeight: '600', color: 'var(--azul-dvs)' }}>
                              {vendaSelecionadaParaModal.vendedor_codigo_pap}
                            </div>
                          </div>
                          <div className="info-item">
                            <div className="info-label">Login PAP</div>
                            <div className="info-value">
                              {vendaSelecionadaParaModal.vendedor_login_pap || '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Observações */}
                            {vendaSelecionadaParaModal.observacao_venda && (
                              <div className="info-section">
                                <h4>📝 Observações da Venda</h4>
                                <div className="observacao-venda-value">
                                  {vendaSelecionadaParaModal.observacao_venda}
                                </div>
                              </div>
                            )}

                            {/* Histórico de Observações de Tratamento e Troca de Status */}
                            {vendaSelecionadaParaModal.observacao_tratamento && (
                              <div className="info-section">
                                <h4>📋 Histórico de Tratamento e Mudanças de Status</h4>
                                <div className="historico-tratamento-container">
                                  {parseObservacoesTratamento(vendaSelecionadaParaModal.observacao_tratamento).map((obs, index) => (
                                    <div
                                      key={index}
                                      className={`observacao-item ${obs.tipo === 'mudanca_status' ? 'mudanca-status' : ''}`}
                                    >
                                      {obs.tipo === 'mudanca_status' ? (
                                        <>
                                          <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '0.5rem',
                                            marginBottom: '0.75rem'
                                          }}>
                                            <span style={{ 
                                              fontSize: '1.2rem',
                                              filter: 'drop-shadow(0 0 4px rgba(6, 102, 255, 0.5))'
                                            }}>🔄</span>
                                            <span style={{ 
                                              fontWeight: '600', 
                                              color: 'var(--azul-dvs)',
                                              fontSize: '0.95rem'
                                            }}>
                                              Mudança de Status
                                            </span>
                                            {obs.dataHora && (
                                              <span style={{ 
                                                marginLeft: 'auto',
                                                fontSize: '0.85rem',
                                                color: 'var(--texto-secundario)',
                                                opacity: 0.8
                                              }}>
                                                {obs.dataHora}
                                              </span>
                                            )}
                                          </div>
                                          <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '0.75rem',
                                            flexWrap: 'wrap'
                                          }}>
                                            <div style={{
                                              padding: '0.4rem 0.8rem',
                                              background: 'rgba(255, 152, 0, 0.2)',
                                              border: '1px solid rgba(255, 152, 0, 0.4)',
                                              borderRadius: '6px',
                                              fontSize: '0.85rem',
                                              color: '#ff9800'
                                            }}>
                                              {obs.statusAnterior}
                                            </div>
                                            <span style={{ color: 'var(--texto-secundario)' }}>→</span>
                                            <div style={{
                                              padding: '0.4rem 0.8rem',
                                              background: 'rgba(6, 102, 255, 0.2)',
                                              border: '1px solid rgba(6, 102, 255, 0.4)',
                                              borderRadius: '6px',
                                              fontSize: '0.85rem',
                                              color: 'var(--azul-dvs)',
                                              fontWeight: '600'
                                            }}>
                                              {obs.statusNovo}
                                            </div>
                                          </div>
                                          {obs.usuario && (
                                            <div style={{ 
                                              marginTop: '0.75rem',
                                              fontSize: '0.85rem',
                                              color: 'var(--texto-secundario)',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '0.5rem'
                                            }}>
                                              <span>👤</span>
                                              <span>Por: <strong>{obs.usuario}</strong></span>
                                            </div>
                                          )}
                                        </>
                                      ) : obs.tipo === 'observacao' ? (
                                        <>
                                          <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '0.5rem',
                                            marginBottom: '0.75rem'
                                          }}>
                                            <span style={{ fontSize: '1.1rem' }}>💬</span>
                                            <span style={{ 
                                              fontWeight: '600', 
                                              color: 'var(--texto-primario)',
                                              fontSize: '0.95rem'
                                            }}>
                                              Observação
                                            </span>
                                            {obs.dataHora && (
                                              <span style={{ 
                                                marginLeft: 'auto',
                                                fontSize: '0.85rem',
                                                color: 'var(--texto-secundario)',
                                                opacity: 0.8
                                              }}>
                                                {obs.dataHora}
                                              </span>
                                            )}
                                          </div>
                                          <div style={{ 
                                            color: 'var(--texto-primario)',
                                            lineHeight: '1.6',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word'
                                          }}>
                                            {obs.texto}
                                          </div>
                                          {obs.usuario && (
                                            <div style={{ 
                                              marginTop: '0.75rem',
                                              fontSize: '0.85rem',
                                              color: 'var(--texto-secundario)',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '0.5rem'
                                            }}>
                                              <span>👤</span>
                                              <span>Por: <strong>{obs.usuario}</strong></span>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <div style={{ 
                                          color: 'var(--texto-primario)',
                                          lineHeight: '1.6',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word'
                                        }}>
                                          {obs.texto}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                  {parseObservacoesTratamento(vendaSelecionadaParaModal.observacao_tratamento).length === 0 && (
                                    <div style={{ 
                                      textAlign: 'center', 
                                      padding: '2rem',
                                      color: 'var(--texto-secundario)',
                                      fontStyle: 'italic'
                                    }}>
                                      Nenhuma observação de tratamento registrada
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Status */}
                            <div className="info-section">
                              <h4>ℹ️ Status e Informações</h4>
                              <div className="info-grid">
                                <div className="info-item">
                                  <div className="info-label">Status</div>
                                  <div className="info-value">
                                    {vendaSelecionadaParaModal.status === 'aguardando_instalacao' && vendaSelecionadaParaModal.data_instalacao && (
                                      <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                                        Data instalação: {formatDateOnly(vendaSelecionadaParaModal.data_instalacao)}
                                      </div>
                                    )}
                                    {vendaSelecionadaParaModal.status === 'aguardando_instalacao' && vendaSelecionadaParaModal.numero_ordem && (
                                      <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                                        Ordem: {vendaSelecionadaParaModal.numero_ordem}
                                      </div>
                                    )}
                                    {vendaSelecionadaParaModal.status === 'concluido' && vendaSelecionadaParaModal.data_conclusao && (
                                      <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                                        Concluído em: {formatDateOnly(vendaSelecionadaParaModal.data_conclusao)}
                                      </div>
                                    )}
                                    <span className={`status-badge ${getStatusClass(vendaSelecionadaParaModal.status)}`}>
                                      {getStatusText(vendaSelecionadaParaModal.status)}
                                    </span>
                                  </div>
                                </div>
                                <div className="info-item">
                                  <div className="info-label">Vendedor</div>
                                  <div className="info-value">{vendaSelecionadaParaModal.vendedor?.nome || 'N/A'}</div>
                                </div>
                                <div className="info-item">
                                  <div className="info-label">Cadastrado em</div>
                                  <div className="info-value">{formatDateTime(vendaSelecionadaParaModal.created_at)}</div>
                                </div>
                                {vendaSelecionadaParaModal.tratado_por && (
                                  <>
                                    <div className="info-item">
                                      <div className="info-label">Tratado por</div>
                                      <div className="info-value">{vendaSelecionadaParaModal.tratador?.nome}</div>
                                    </div>
                                    <div className="info-item">
                                      <div className="info-label">Tratado em</div>
                                      <div className="info-value">{formatDateTime(vendaSelecionadaParaModal.tratado_em)}</div>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Informações de Viabilidade */}
                            {vendaSelecionadaParaModal.operadora_selecionada && (
                              <div className="info-section">
                                <h4>📡 Informações de Viabilidade</h4>
                                <div className="info-grid">
                                  <div className="info-item">
                                    <div className="info-label">Operadora Selecionada</div>
                                    <div className="info-value" style={{ fontWeight: '600' }}>
                                      {vendaSelecionadaParaModal.operadora_selecionada.charAt(0).toUpperCase() + vendaSelecionadaParaModal.operadora_selecionada.slice(1)}
                                    </div>
                                  </div>
                                  {vendaSelecionadaParaModal.operadora_viabilidade_data && (
                                    <div className="info-item">
                                      <div className="info-label">Data da Consulta</div>
                                      <div className="info-value">
                                        {formatDateTime(vendaSelecionadaParaModal.operadora_viabilidade_data)}
                                      </div>
                                    </div>
                                  )}
                                  {vendaSelecionadaParaModal.operadora_viabilidade_json && (
                                    <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                                      <div className="info-label">Status de Viabilidade</div>
                                      <div className="info-value">
                                        {(() => {
                                          try {
                                            let viabilidade = vendaSelecionadaParaModal.operadora_viabilidade_json;
                                            
                                            // Se for string, fazer parse
                                            if (typeof viabilidade === 'string') {
                                              try {
                                                viabilidade = JSON.parse(viabilidade);
                                                // Se o resultado ainda for string (double-encoded), fazer parse novamente
                                                if (typeof viabilidade === 'string') {
                                                  viabilidade = JSON.parse(viabilidade);
                                                }
                                              } catch (parseError) {
                                                console.error('Erro ao fazer parse do JSON:', parseError);
                                                return <span style={{ color: '#dc3545' }}>Erro ao processar JSON de viabilidade</span>;
                                              }
                                            }
                                            
                                            console.log('📋 Viabilidade JSON parseado (Modal Detalhes):', viabilidade);
                                            
                                            // Se viabilidade é null ou undefined
                                            if (!viabilidade) {
                                              return <span style={{ color: '#868e96' }}>Dados de viabilidade não disponíveis</span>;
                                            }
                                            
                                            // Verificar se tem operadoras diretamente ou dentro de uma propriedade
                                            let operadoras = null;
                                            if (viabilidade.operadoras && typeof viabilidade.operadoras === 'object') {
                                              operadoras = viabilidade.operadoras;
                                            } else if (viabilidade.data && viabilidade.data.operadoras) {
                                              operadoras = viabilidade.data.operadoras;
                                            } else if (typeof viabilidade === 'object' && !Array.isArray(viabilidade)) {
                                              const keys = Object.keys(viabilidade);
                                              const hasOperadoraStructure = keys.some(key => {
                                                const val = viabilidade[key];
                                                return val && typeof val === 'object' && ('coverage' in val);
                                              });
                                              if (hasOperadoraStructure) {
                                                operadoras = viabilidade;
                                              }
                                            }
                                            
                                            if (!operadoras || (typeof operadoras === 'object' && Object.keys(operadoras).length === 0)) {
                                              console.warn('Viabilidade sem operadoras válidas:', viabilidade);
                                              return (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                  <span style={{ color: '#868e96' }}>Dados de viabilidade não contêm operadoras</span>
                                                  <details style={{ fontSize: '0.85rem', color: '#868e96' }}>
                                                    <summary style={{ cursor: 'pointer' }}>Ver estrutura do JSON</summary>
                                                    <pre style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#1e1e1e', borderRadius: '4px', overflow: 'auto', maxHeight: '200px' }}>
                                                      {JSON.stringify(viabilidade, null, 2)}
                                                    </pre>
                                                  </details>
                                                </div>
                                              );
                                            }

                                            // Normalizar nome da operadora (case-insensitive, remover espaços extras)
                                            const operadoraSelecionadaNormalizada = vendaSelecionadaParaModal.operadora_selecionada 
                                              ? vendaSelecionadaParaModal.operadora_selecionada.toLowerCase().trim()
                                              : null;

                                            // Procurar operadora no JSON (case-insensitive)
                                            let operadora = null;
                                            if (operadoraSelecionadaNormalizada && operadoras) {
                                              // Tentar encontrar pelo nome exato primeiro
                                              operadora = operadoras[vendaSelecionadaParaModal.operadora_selecionada];
                                              
                                              // Se não encontrar, procurar case-insensitive
                                              if (!operadora) {
                                                const operadorasKeys = Object.keys(operadoras);
                                                const operadoraEncontrada = operadorasKeys.find(key => 
                                                  key.toLowerCase().trim() === operadoraSelecionadaNormalizada
                                                );
                                                if (operadoraEncontrada) {
                                                  operadora = operadoras[operadoraEncontrada];
                                                }
                                              }
                                            }

                                            if (!operadora) {
                                              console.warn('Operadora não encontrada na viabilidade:', {
                                                operadoraSelecionada: vendaSelecionadaParaModal.operadora_selecionada,
                                                operadoras: Object.keys(operadoras)
                                              });
                                              return <span style={{ color: '#868e96' }}>Operadora não encontrada nos dados de viabilidade</span>;
                                            }
                                            
                                            const hasCoverage = operadora.coverage === true;
                                            console.log('✅ Viabilidade da operadora:', { operadora, hasCoverage });
                                            
                                            return (
                                              <span className={`status-badge ${hasCoverage ? 'status-ativo' : 'status-inativo'}`} style={{ 
                                                background: hasCoverage ? '#4caf50' : '#ff9800',
                                                color: '#000 !important',
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '4px',
                                                fontWeight: '600'
                                              }}>
                                                {hasCoverage ? '✓ Viabilidade Confirmada' : '⚠ Sem Viabilidade Confirmada'}
                                              </span>
                                            );
                                          } catch (e) {
                                            console.error('Erro ao processar viabilidade:', e);
                                            return <span style={{ color: '#dc3545' }}>Erro ao processar dados de viabilidade</span>;
                                          }
                                        })()}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Envio de Aceite */}
                            <div className="info-section">
                              <h4>📧 Envio de Aceite</h4>
                              <div className="info-grid">
                                <div className="info-item">
                                  <div className="info-label">Operadora</div>
                                  <div className="info-value">
                                    {vendaSelecionadaParaModal.envio_aceite ? (() => {
                                      const parts = vendaSelecionadaParaModal.envio_aceite.split(':');
                                      return parts.length > 0 
                                        ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
                                        : '-';
                                    })() : 'Não informado'}
                                  </div>
                                </div>
                                <div className="info-item">
                                  <div className="info-label">Meios de Envio</div>
                                  <div className="info-value">
                                    {vendaSelecionadaParaModal.envio_aceite ? (() => {
                                      const parts = vendaSelecionadaParaModal.envio_aceite.split(':');
                                      if (parts.length < 2) return '-';
                                      
                                      const meios = parts[1].split(',');
                                      return <MeiosEnvioList meios={meios} />;
                                    })() : 'Não informado'}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Documentos Anexos */}
                            {vendaSelecionadaParaModal.documento_anexo && (
                              <div className="info-section">
                                <h4>📎 Documentos Anexos</h4>
                                <div className="info-value" style={{ marginTop: '1rem' }}>
                                  {(() => {
                                    // Tentar fazer parse do JSON (múltiplos arquivos) ou usar como string (arquivo único - compatibilidade)
                                    let documentos = [];
                                    try {
                                      const parsed = JSON.parse(vendaSelecionadaParaModal.documento_anexo);
                                      documentos = Array.isArray(parsed) ? parsed : [parsed];
                                    } catch (e) {
                                      // Se não for JSON, tratar como string única (compatibilidade com dados antigos)
                                      documentos = [vendaSelecionadaParaModal.documento_anexo];
                                    }
                                    
                                    // Função para construir URL correta do documento
                                    const getDocumentoUrl = (documentoPath) => {
                                      // Se o caminho já começa com http, retornar como está
                                      if (documentoPath.startsWith('http://') || documentoPath.startsWith('https://')) {
                                        return documentoPath;
                                      }
                                      
                                      // Usar a mesma URL base da API configurada no axios
                                      let baseUrl = getApiBaseUrl();
                                      
                                      // Remover /api do final se existir (os uploads são servidos na raiz do servidor)
                                      baseUrl = baseUrl.replace(/\/api\/?$/, '');
                                      
                                      // Remover barra final se existir
                                      baseUrl = baseUrl.replace(/\/$/, '');
                                      
                                      // Garantir que o caminho tenha o formato correto
                                      // Se não começar com "uploads/", adicionar
                                      let path = documentoPath;
                                      if (!path.startsWith('uploads/') && !path.startsWith('/uploads/')) {
                                        // Se tiver apenas o nome do arquivo, adicionar o caminho completo
                                        if (!path.includes('/')) {
                                          path = `uploads/documentos/${path}`;
                                        }
                                      }
                                      
                                      // Garantir que o caminho comece com /
                                      path = path.startsWith('/') ? path : `/${path}`;
                                      
                                      const fullUrl = `${baseUrl}${path}`;
                                      console.log('📂 Construindo URL do documento:', {
                                        documentoPath,
                                        baseUrl,
                                        path,
                                        fullUrl
                                      });
                                      return fullUrl;
                                    };
                                    
                                    return (
                                      <>
                                        {documentos.map((documentoPath, index) => {
                                          const documentoUrl = getDocumentoUrl(documentoPath);
                                          const isImage = /\.(jpg|jpeg|png)$/i.test(documentoPath);
                                          const isPdf = /\.pdf$/i.test(documentoPath);
                                          const fileName = documentoPath.split('/').pop();
                                          
                                          // Função para fazer download
                                          const handleDownload = async (e) => {
                                            e.preventDefault();
                                            console.log('📥 Tentando baixar:', documentoUrl);
                                            
                                            try {
                                              // Tentar usar fetch primeiro
                                              const response = await fetch(documentoUrl, {
                                                method: 'GET',
                                                headers: {
                                                  'Accept': '*/*'
                                                }
                                              });
                                              
                                              if (!response.ok) {
                                                console.error('❌ Erro na resposta:', response.status, response.statusText);
                                                throw new Error(`Arquivo não encontrado (${response.status})`);
                                              }
                                              
                                              const blob = await response.blob();
                                              const url = window.URL.createObjectURL(blob);
                                              const link = document.createElement('a');
                                              link.href = url;
                                              link.download = fileName;
                                              document.body.appendChild(link);
                                              link.click();
                                              document.body.removeChild(link);
                                              window.URL.revokeObjectURL(url);
                                              console.log('✅ Download iniciado com sucesso');
                                            } catch (error) {
                                              console.error('❌ Erro ao baixar arquivo:', error);
                                              // Se fetch falhar, tentar abrir em nova aba como fallback
                                              alert(`⚠️ Não foi possível fazer o download automático. Abrindo em nova aba...\n\nErro: ${error.message}`);
                                              window.open(documentoUrl, '_blank');
                                            }
                                          };
                                          
                                          return (
                                            <div key={index} style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(42, 42, 42, 0.3)', borderRadius: '8px' }}>
                                              <div style={{ marginBottom: '0.5rem', fontWeight: '600', color: 'var(--texto-primario)' }}>
                                                📄 Documento {documentos.length > 1 ? `${index + 1}` : ''}: {fileName}
                                              </div>
                                              {isImage && (
                                                <div style={{ marginBottom: '0.75rem', position: 'relative' }}>
                                                  <img 
                                                    src={documentoUrl}
                                                    alt={`Documento anexo ${index + 1}`}
                                                    style={{ 
                                                      maxWidth: '100%', 
                                                      maxHeight: '500px', 
                                                      border: '1px solid var(--border-color)',
                                                      borderRadius: '8px',
                                                      cursor: 'pointer',
                                                      objectFit: 'contain',
                                                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                      display: 'block'
                                                    }}
                                                    onClick={() => window.open(documentoUrl, '_blank')}
                                                    onError={(e) => {
                                                      // Se a imagem falhar ao carregar, mostrar mensagem
                                                      const img = e.target;
                                                      img.style.display = 'none';
                                                      const parent = img.parentNode;
                                                      // Verificar se já existe mensagem de erro
                                                      if (!parent.querySelector('.image-error-message')) {
                                                        const errorDiv = document.createElement('div');
                                                        errorDiv.className = 'image-error-message';
                                                        errorDiv.style.cssText = 'padding: 2rem; text-align: center; color: var(--texto-secundario); background: rgba(255, 152, 0, 0.1); border-radius: 8px; border: 1px solid rgba(255, 152, 0, 0.3);';
                                                        errorDiv.innerHTML = `
                                                          <div style="margin-bottom: 0.5rem;">⚠️ Imagem não pôde ser carregada</div>
                                                          <div style="font-size: 0.85rem; opacity: 0.8;">Use os botões abaixo para abrir ou baixar o documento</div>
                                                        `;
                                                        parent.appendChild(errorDiv);
                                                      }
                                                    }}
                                                  />
                                                </div>
                                              )}
                                              {isPdf && (
                                                <div style={{ marginBottom: '0.75rem' }}>
                                                  <iframe
                                                    src={documentoUrl}
                                                    style={{
                                                      width: '100%',
                                                      height: '600px',
                                                      border: '1px solid var(--border-color)',
                                                      borderRadius: '8px'
                                                    }}
                                                    title={`Documento PDF ${index + 1}`}
                                                    onError={() => {
                                                      console.error('Erro ao carregar PDF');
                                                    }}
                                                  />
                                                </div>
                                              )}
                                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                                                <button
                                                  onClick={() => window.open(documentoUrl, '_blank')}
                                                  style={{
                                                    display: 'inline-block',
                                                    padding: '0.5rem 1rem',
                                                    background: 'var(--azul-dvs)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '0.9rem',
                                                    cursor: 'pointer',
                                                    textDecoration: 'none'
                                                  }}
                                                >
                                                  🔗 Abrir em Nova Aba
                                                </button>
                                                <button
                                                  onClick={handleDownload}
                                                  type="button"
                                                  style={{
                                                    display: 'inline-block',
                                                    padding: '0.5rem 1rem',
                                                    background: '#28a745',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '0.9rem',
                                                    cursor: 'pointer'
                                                  }}
                                                >
                                                  📥 Baixar Documento
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      </ModalPortal>
                    )}
                  </div>
                  
                  <div style={{ padding: '1rem', borderTop: '1px solid var(--border-color)', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button onClick={fecharHistoricoVenda} className="btn-secondary">
                      Fechar
                    </button>
                  </div>
                </div>
              </div>
              </ModalPortal>
            )}
          </div>
          </ModalPortal>
        )}
      </div>

      {validacaoOkModal === 'confirm' && selectedVenda && (
        <ModalPortal>
          <div className="modal-overlay modal-overlay--stacked" onClick={() => setValidacaoOkModal(null)}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
              <div className="modal-header-venda">
                <h2>Validação OK</h2>
                <span className="close-modal" onClick={() => setValidacaoOkModal(null)}>×</span>
              </div>
              <div className="modal-body-venda">
                <p style={{ fontSize: '1.05rem', marginBottom: '1.5rem', textAlign: 'center' }}>
                  A venda está OK ou precisa editar?
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <button type="button" className="btn-primary" onClick={handleValidacaoOkConfirm}>
                    OK
                  </button>
                  <button type="button" className="btn-secondary" onClick={handleValidacaoOkEditar}>
                    Editar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {editandoValidacaoOk && (
        <EditarVendaModal
          venda={editandoValidacaoOk}
          user={user}
          modoValidacaoOk
          stacked
          onClose={() => setEditandoValidacaoOk(null)}
          onSaved={handleValidacaoOkEditSaved}
        />
      )}
    </Layout>
  );
};

export default FilaTratamento;
